// Native generic item card for held inventory, catalog, form and attached-object consumers.
// The shared view owns cost/weight/requirements; list-display owns the property list.
// Form controls are mounted beside this card by the route and survive its paints.

import { encounterAuthoringActive, encounterPreviewSurface } from '../shared/encounter-surface';
import { entityBasePending } from '../shared/compile-fetched-entity';
import { ENTITY_CARD_LOADING } from './entity-card-state';
import {
    itemsCol, inventoryField, encounterCreatureField, layerOf, layerHas, layerEntriesOf, posCoord,
    scalarLayerSlot, layerListEntryUuid, defaultProviderCarriers,
    hasPlayerPermission, hasCampaignPermission, picturesOf, itemVideos, plPretty, plExact, computedValue,
} from '../shared/array-types';
import { isUuid, makeNumberPretty, nameOf, itemHref } from './helpers';
import { visibleInventoryEncounterLinks } from './encounter-links-display';
import { findInventoryInstanceNode, findCasterKeyForLock, nodeHasStatusItemId, LOCKED_STATUS_ITEM_ID, CRAFTING_PROGRESS_STATUS_ITEM_ID } from './find-cached-item';
import { targetIsHidden as inventoryIsHidden } from '../janus-engine/display/marker-visibility';
import type { CachedItemNode } from './find-cached-item';
import { buildContainerLootGenHtml } from './container-loot-gen';
import { bookActionMountHtml, gatherControlKind, gatherControlsHtml, islandPlaceholder } from './islands';
import { hasBookReadingMechanics } from '../janus-engine/display/object-card-actions-21';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { isCreatureSubTypeItemType } from '../shared/item-type-badge';
import { buildCarousel } from '../shared/build-carousel';
import { parseText } from '../shared/parse-text';
import { displaySpellCastingTime } from '../shared/spell-casting-time';
import { sourceFooterHtml } from './card-shell';
import { openMeshGallery } from './mesh-gallery';
import { renderSpellVfxMarker, openSpellVfxPreview } from './spell-vfx-marker';
import { ftCasterHoldsRow, throwObjectPlan } from './throw-object';
import { actorCanReachTarget } from '../shared/proximity-browser';
import { renderCorpseBodySections } from './corpse-body';
import { queueInventoryFlag, type Bit, type InventoryFlagAnswer, type InventoryFlagKind } from '../janus-engine/inventory/flag-adjust.js';
import type { ReferencedNames } from './types';
import { refuseUnported } from '../janus-engine/unported.js';
import { inventoryRowTotals } from '../janus-engine/display/inventory-totals-21';
import { itemCardView, itemCardReferenceId, itemCardCanDisenchant } from '../janus-engine/display/item-card-rules31';
import { pickTrapTrigger } from './trap-trigger';

/** Shared by the TS card and its retained table-template confirmation button. */
export async function requestObjectDestruction(input: { campaignId: string; encounterId: string; inventoryId: string; casterId?: string }): Promise<boolean> {
    const entry = (window as Window & { ftTryCommitObjectDestruction?: (value: typeof input) => Promise<string | null> }).ftTryCommitObjectDestruction;
    if (typeof entry !== 'function') { refuseUnported('object-rubble:browser-resolver-unavailable', input); return false; }
    try {
        const outcome = await entry(input);
        if (outcome === null) { refuseUnported('object-rubble:browser-plan-unported', input); return false; }
        if (outcome === 'handled') { ocCall('closeSideInfoPanel'); ocCall('openSideLog'); return true; }
        return false;
    } catch (error) {
        // The browser may already have drawn/sent a lazy save. Never execute a second destruction.
        refuseUnported('object-rubble:browser-request-unconfirmed', { ...input, error: String(error) });
        return false;
    }
}
Object.assign(window, { ftTurnObjectToRubble: requestObjectDestruction });

type ObjectCardBlob = CachedItemNode;

type ObjectCardWindow = Record<string, unknown> & {
    ftFindFullNestedItem?: (id: string) => unknown;
};

function recordOf(value: unknown): ObjectCardBlob | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as ObjectCardBlob
        : null;
}

function objectCardWindow(): ObjectCardWindow {
    return window as unknown as ObjectCardWindow;
}

function resolvedDefaultProviderCarriers(arr: ObjectCardBlob): ObjectCardBlob[] {
    const out: ObjectCardBlob[] = [];
    const findFull = objectCardWindow().ftFindFullNestedItem;
    for (const carrier of defaultProviderCarriers(arr)) {
        const direct = recordOf(carrier);
        if (direct) {
            out.push(direct);
            continue;
        }
        if (typeof carrier === 'string' && typeof findFull === 'function') {
            const resolved = recordOf(findFull(carrier));
            if (resolved) out.push(resolved);
        }
    }
    return out;
}

function inheritedLayer(arr: ObjectCardBlob, key: string): unknown {
    const own = layerOf(arr, key);
    if (own !== null && own !== undefined && own !== '') return own;
    for (const carrier of resolvedDefaultProviderCarriers(arr)) {
        const value = layerOf(carrier, key);
        if (value !== null && value !== undefined && value !== '') return value;
    }
    const classification = classificationCarrierNode(arr);
    if (classification) {
        const value = layerOf(classification, key);
        if (value !== null && value !== undefined && value !== '') return value;
    }
    return own;
}

function computedRecord(arr: ObjectCardBlob, key: string): ObjectCardBlob | null {
    return recordOf(recordOf(arr.computed)?.[key]);
}

// ── small local read shims matching the PHP call sites ──────────────────────────
function itemType(arr: ObjectCardBlob): string { return String(itemsCol(arr, 'item_type') ?? ''); }
// World-fixed rows never get carry verbs (twin of the server's FT_CASTER_NON_CARRYABLE_ITEM_TYPES + the
// terrain types ft_throw_object_gate refuses); the throw-object module keeps the same list.
const NON_THROWABLE_CARD_TYPES = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
    'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite', 'map_marker', 'wall_run']);
function invDeletedFlag(arr: ObjectCardBlob): boolean { return !!inventoryField(arr, 'inventory_deleted') || !!itemsCol(arr, 'item_deleted'); }

function ocCall(name: string, ...args: unknown[]): unknown {
    const fn = objectCardWindow()[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function ocAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-oc-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-itemId` reads back as dataset.ftItemid,
        // NOT dataset.ftItemId. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

// INV2 S1 / S1b (2026-09-27): the wares and Hide / Reveal buttons commit an explicit desired flag through the shared per-row queue
// (the websocket bundle's window.ftCommitInventoryFlag when loaded, so peer frames move the same confirmed floor; else this
// bundle's own copy of the queue). Hidden paints after its status preflight. Null = not a flag button.
type FlagCommit = (input: { kind: InventoryFlagKind; encounterId: string; inventoryId: string; actorId?: string | null; desired: Bit;
    seed: { value: Bit; possessId: string }; onPrepared?: () => void }) => Promise<InventoryFlagAnswer>;
const FLAG_BUTTONS: ReadonlyArray<[prefix: string, kind: InventoryFlagKind, desired: Bit]> = [
    ['btn_wares_on_inventory_', 'wares', 1], ['btn_wares_off_inventory_', 'wares', 0],
    ['btn_hide_creature_', 'hidden', 1], ['btn_reveal_creature_', 'hidden', 0],
];
const flagPaintGeneration = new Map<string, number>();
function inventoryFlagToggle(el: HTMLElement, payload: Record<string, string>): Promise<void> | null {
    const match = FLAG_BUTTONS.find(([prefix]) => el.id.startsWith(prefix));
    const inventoryId = String(payload.inventory_id ?? '').trim();
    const possessId = String(payload.possess_id ?? '').trim();
    if (!match) return null;
    const [, kind, desired] = match;
    // No legacy fallback (INV2 2026-09-28: the legacy flag endpoints are retired). The encounter may be absent — the server derives it
    // from the row (hidden) or the acting creature (wares); the row, its holder and (wares) the acting caster are required.
    if (!isUuid(inventoryId) || !isUuid(possessId) || (kind === 'wares' && !isUuid(String(payload.caster_id ?? '')))) {
        ocCall('displayWarning', kind === 'wares' ? 'Choose the character acting before marking wares.' : 'This object could not be updated from here — reopen it.');
        return Promise.resolve();
    }
    // Only the card's OWN encounter context; never the page's active encounter (a row of another encounter would then be refused even
    // for its GM) — absent, the server derives it from the row (hidden) or the acting creature (wares).
    const encounterId = isUuid(String(payload.encounter_id ?? '')) ? String(payload.encounter_id) : '';
    const hide = document.getElementById(el.dataset.ftHideTarget || '');
    const show = document.getElementById(el.dataset.ftShowTarget || '');
    const paint = (landed: boolean): void => {
        if (hide) hide.style.display = landed ? 'none' : (el.dataset.ftShowDisplay || 'block');
        if (show) show.style.display = landed ? (el.dataset.ftShowDisplay || 'block') : 'none';
    };
    if (kind === 'wares') paint(true);
    // Only the NEWEST click on this row may repaint on a refusal (Codex review INV2 S1 #9): an older refusal must not undo a later paint.
    const genKey = kind + ':' + inventoryId.toUpperCase();
    const gen = (flagPaintGeneration.get(genKey) ?? 0) + 1;
    flagPaintGeneration.set(genKey, gen);
    const commit = (window as unknown as { ftCommitInventoryFlag?: FlagCommit }).ftCommitInventoryFlag ?? queueInventoryFlag;
    // The clicked button IS the state the card showed: Hide / "add to wares" is offered only while the flag is off.
    return commit({ kind, encounterId, inventoryId, actorId: kind === 'wares' ? payload.caster_id : null, desired,
        seed: { value: desired === 1 ? 0 : 1, possessId }, onPrepared: () => { if (kind === 'hidden' && flagPaintGeneration.get(genKey) === gen) paint(true); } }).then(answer => {
        if (answer.ok || flagPaintGeneration.get(genKey) !== gen) return;
        const saved = answer.saved;
        // A refusal adopts SQL truth when the server named it; otherwise the card goes back to what it showed.
        paint(saved ? saved.value === desired : false);
        const message = answer.status === 403 ? (kind === 'hidden' ? 'Only the GM can hide or reveal this object.' : String(answer.error || 'Permission required.'))
            : answer.status === 409 ? 'That item changed elsewhere; its current state was restored.' : 'The change could not be saved: ' + String(answer.error || answer.status);
        ocCall('displayWarning', message);
    }, () => { if (flagPaintGeneration.get(genKey) === gen) paint(false); ocCall('displayWarning', 'The change could not be saved.'); });
}

function ocInfoAttrs(itemId: string): string {
    return ocAttrs('open-info', { itemId });
}

export function invokeObjectCardAction(el: HTMLElement, event: Event): void {
    const action = el.dataset.ftOcAction || '';
    if (action === 'open-info') { event.preventDefault(); ocCall('openItemInfo', el.dataset.ftItemId || ''); return; }
    if (action === 'open-power-level') { event.preventDefault(); ocCall('openPowerLevelInfo', el.dataset.ftItemId || ''); return; }
    if (action === 'open-mesh-gallery') {
        event.preventDefault();
        void openMeshGallery(el.dataset.ftItemId || '', el.dataset.ftItemName || '');
        return;
    }
    if (action === 'open-spell-vfx') {
        event.preventDefault();
        void openSpellVfxPreview(el);
        return;
    }
    if (action === 'show-confirm') {
        event.preventDefault();
        const hide = document.getElementById(el.dataset.ftHideTarget || '');
        const show = document.getElementById(el.dataset.ftShowTarget || '');
        if (hide) hide.style.display = 'none';
        if (show) show.style.display = 'inline-block';
        const rubbleConfirm = 'btn_confirm_turn_rubble_inventory_';
        if ((el.dataset.ftShowTarget || '').startsWith(rubbleConfirm)) {
            (window as Window & { ftPrefetchObjectDestruction?: (input: { inventoryId: string }) => void })
                .ftPrefetchObjectDestruction?.({ inventoryId: (el.dataset.ftShowTarget || '').slice(rubbleConfirm.length) });
        }
        return;
    }
    if (action === 'query-data') {
        event.preventDefault();
        const payload: Record<string, string> = {};
        for (const pair of (el.dataset.ftPayload || '').split('|')) {
            const idx = pair.indexOf('=');
            if (idx <= 0) continue;
            payload[pair.slice(0, idx)] = pair.slice(idx + 1);
        }
        void ocCall('queryData', el.dataset.ftUrl || '', el.dataset.ftTarget || 'div_sidelog_warning', payload);
        return;
    }
    if (action === 'query-and-close') {
        event.preventDefault();
        const payload: Record<string, string> = {};
        for (const pair of (el.dataset.ftPayload || '').split('|')) {
            const idx = pair.indexOf('=');
            if (idx <= 0) continue;
            payload[pair.slice(0, idx)] = pair.slice(idx + 1);
        }
        const runLegacy = (): void => {
            void Promise.resolve(ocCall('queryData', el.dataset.ftUrl || '', el.dataset.ftTarget || 'div_sidelog_warning', payload)).then(() => {
                ocCall('closeSideInfoPanel');
                ocCall('openSideLog');
            });
        };
        if (el.id.startsWith('btn_confirm_turn_rubble_inventory_')) {
            if (el.dataset.ftBusy === '1') return; // the pile's definition may still be loading
            el.dataset.ftBusy = '1';
            void requestObjectDestruction({ campaignId: payload.campaign_id ?? '', encounterId: payload.encounter_id ?? '', inventoryId: payload.inventory_id ?? '', casterId: payload.caster_id ?? '' })
                .finally(() => { delete el.dataset.ftBusy; });
            return;
        }
        runLegacy();
        return;
    }
    if (action === 'toggle-query') {
        event.preventDefault();
        const payload: Record<string, string> = {};
        for (const pair of (el.dataset.ftPayload || '').split('|')) {
            const idx = pair.indexOf('=');
            if (idx <= 0) continue;
            payload[pair.slice(0, idx)] = pair.slice(idx + 1);
        }
        // INV2 S1 / S1b (2026-09-27): wares and hide / reveal are client-truth flag commits (janus-engine/inventory/flag-adjust.ts →
        // inventory_flag_truth_commit_inc.php): paint the pair now, commit the explicit desired value, restore on a refusal.
        const flag = inventoryFlagToggle(el, payload);
        if (flag) { void flag; return; }
        void Promise.resolve(ocCall('queryData', el.dataset.ftUrl || '', el.dataset.ftTarget || 'div_sidelog_warning', payload)).then(() => {
            const hide = document.getElementById(el.dataset.ftHideTarget || '');
            const show = document.getElementById(el.dataset.ftShowTarget || '');
            if (hide) hide.style.display = 'none';
            if (show) show.style.display = el.dataset.ftShowDisplay || 'block';
        });
        return;
    }
    if (action === 'delete-inventory') {
        event.preventDefault();
        const payload: Record<string, string> = {};
        for (const pair of (el.dataset.ftPayload || '').split('|')) {
            const idx = pair.indexOf('=');
            if (idx <= 0) continue;
            payload[pair.slice(0, idx)] = pair.slice(idx + 1);
        }
        const bridge = window as Window & { ftTryDeleteInventory?: (input: { inventoryId: string; encounterId: string; actorId: string; log: boolean }) => Promise<'handled' | 'refused'> };
        if (!bridge.ftTryDeleteInventory) { ocCall('displayWarning', 'inventory-delete:browser-required'); return; }
        void bridge.ftTryDeleteInventory({ inventoryId: payload.inventory_id || '', encounterId: payload.encounter_id || '',
            actorId: payload.caster_id || '', log: !encounterAuthoringActive() }).then(result => {
            if (result !== 'handled') return;
            ocCall('closeSideInfoPanel');
            ocCall('openSideLog');
        });
        return;
    }
    if (action === 'open-edit-inventory') { event.preventDefault(); ocCall('openEditInventoryForm', el.dataset.ftInventoryId || ''); return; }
    if (action === 'open-inventory-status-effects') { event.preventDefault(); ocCall('openInventoryStatusEffects', el.dataset.ftInventoryId || ''); return; }
    if (action === 'use-key-lock') { event.preventDefault(); ocCall('useKeysOnLock', el.dataset.ftInventoryId || '', el.dataset.ftInventoryId || '', 'lock'); return; }
    // Trap verbs (Trap minigame wave 2026-07-31): Disarm opens the minigame (trap-disarm bundle);
    // Discern/Arm POST their endpoints directly. All three re-validate reach/tools server-side.
    if (action === 'trap-disarm') { event.preventDefault(); ocCall('openTrapDisarmMinigame', el.dataset.ftInventoryId || ''); return; }
    if (action === 'trap-discern') { event.preventDefault(); void ftTrapVerb('discern_trap_inc.php', el.dataset.ftInventoryId || ''); return; }
    if (action === 'trap-arm') { event.preventDefault(); void ftTrapVerb('arm_trap_inc.php', el.dataset.ftInventoryId || ''); return; }
    if (action === 'trap-trigger') { event.preventDefault(); pickTrapTrigger(el.dataset.ftInventoryId || ''); return; }
    if (action === 'consume') { event.preventDefault(); ocCall('useAbilityVariable1', el.dataset.ftInventoryId || '', 'consume'); return; }
    // Carry verbs → object-card/throw-object.ts (same bundle, window-attached for the radial + out-of-bundle callers).
    if (action === 'drop-inventory') { event.preventDefault(); ocCall('ftDropInventoryAtFeet', el.dataset.ftInventoryId || ''); ocCall('closeSideInfoPanel'); return; }
    if (action === 'pick-up-inventory') { event.preventDefault(); ocCall('ftPickUpInventory', el.dataset.ftInventoryId || ''); ocCall('closeSideInfoPanel'); return; }
    if (action === 'throw-inventory') { event.preventDefault(); ocCall('ftThrowObjectStart', el.dataset.ftInventoryId || ''); ocCall('closeSideInfoPanel'); return; }
    if (action === 'disenchant') {
        event.preventDefault();
        const inventoryId = el.dataset.ftInventoryId || '';
        ocCall('ftUseAbilityAtExplicitTarget', 'FDCAAAC1-9502-C9F4-55C3-501C7D65192F', inventoryId);
        return;
    }
    if (action === 'copy-enchantment-template') {
        event.preventDefault();
        const itemId = el.dataset.ftItemId || '';
        const input = document.getElementById(`input_hidden_enchantment_template_id_${itemId}`) as HTMLInputElement | null;
        if (input) ocCall('copyInputToClipboard', input.id);
        const result = document.getElementById(`div_refresh_${itemId}`);
        if (result) result.textContent = 'Copied!';
    }
}
document.addEventListener('click', event => {
    const el = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ft-oc-action]') : null;
    if (el) invokeObjectCardAction(el, event);
});

// Rune-eligible item types — mirror of PHP ft_rune_icon_eligible() (spell + the AbilityType family,
// minus the ability_choice selector wrapper). These get a header rune MEDALLION (the .ft-rune-card
// header in table_item_object_inc.php), not just the small flat type icon.
const RUNE_ELIGIBLE_TYPES = new Set<string>([
    'spell', 'passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability',
    'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine',
    'lair_ability',
]);
function runeIsEligible(arr: ObjectCardBlob): boolean { return RUNE_ELIGIBLE_TYPES.has(itemType(arr)); }

// Port of ft_rune_icon_mount_html() — emits the .ft-rune-mount placeholder carrying {items,
// item_layer_types} as a non-executable <script type="application/json">, which window.ftMountRuneIcons()
// hydrates into the medallion SVG (window.RuneIcon). Size 96 → full LOD, matching the PHP card header.
// Returns '' for ineligible types or a blob with no layers (a typeless/layerless rune is meaningless).
function runeIconMountHtml(arr: ObjectCardBlob, size = 96): string {
    if (!runeIsEligible(arr)) return '';
    const itemLayerTypes = recordOf(arr.item_layer_types);
    if (!itemLayerTypes) return '';
    const payload = {
        items: recordOf(arr.items) ?? {},
        item_layer_types: itemLayerTypes,
        catalog: recordOf(arr.catalog) ?? {},
    };
    const json = JSON.stringify(payload).replace(/<\//g, '<\\/');
    return `<div class="ft-rune-mount" data-rune-size="${size}"><script type="application/json" class="ft-rune-blob">${json}</script></div>`;
}
function itemId(arr: ObjectCardBlob): string { return String(itemsCol(arr, 'item_id') ?? ''); }
/** The PL heal root of a card: an inventory INSTANCE heals under its own inventory id (its `inventory` bucket is the row), else the item. */
function plRootIdOf(arr: ObjectCardBlob): string {
    const inv = recordOf((arr as Record<string, unknown>).inventory);
    const iid = inv && typeof inv.inventory_id === 'string' ? inv.inventory_id : '';
    return (iid || itemId(arr)).toUpperCase();
}
/** The priced PL pill: the plPretty() abbreviation ("2.2k") on the badge, the exact figure on the tooltip, the click opens the full
 *  PL breakdown. PHP twin: ft_pl_pretty(). */
function renderPricedPlPill(pl: unknown, forItemId: string, instanceRoot = ''): string {
    // an inventory INSTANCE's pill names its own root: it shows the nested copy's figure (the template's) until the page resolves the
    // instance's own saved PL (window.ftPlResolveInstances, c1-possessor-shadow.ts) and swaps it in through ft:pl-healed below
    const inst = instanceRoot ? ` data-ft-pl-root="${escapeHtml(instanceRoot)}" data-ft-pl-item="${escapeHtml(forItemId)}" data-ft-pl-instance="1"` : '';
    return `<span class="d-inline-block" role="button" data-bs-toggle="tooltip" data-bs-original-title="Power Level: ${escapeHtml(plExact(pl))}" ${ocAttrs('open-power-level', { itemId: forItemId })}${inst}><span class="badge bg-white text-dark">${escapeHtml(plPretty(pl))}</span></span>`;
}
/** The inventory id of a card that shows an inventory INSTANCE (its PL is its own, not its template's), else ''. */
function plInstanceRootOf(arr: ObjectCardBlob): string {
    const root = plRootIdOf(arr);
    return isInventory(arr) && root !== itemId(arr).toUpperCase() ? root : '';
}
/** Ask the page to resolve the instance pills just rendered (after the caller inserted the HTML). */
function schedulePlInstanceResolve(): void {
    if (typeof window === 'undefined') return;
    setTimeout(() => { try { (window as unknown as { ftPlResolveInstances?: () => void }).ftPlResolveInstances?.(); } catch { /* display only */ } }, 0);
}
// The page's PL healer (janus-engine/pl/heal.ts) announces a SAVED price: repaint every pending pill for that root in place, so the
// card does not keep reading "pending" until a reload (the value is already in Redis). A zero price shows no pill, like a render.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    const host = window as unknown as { __ftPlHealedPillHook?: boolean };
    if (!host.__ftPlHealedPillHook) {
        host.__ftPlHealedPillHook = true;
        window.addEventListener('ft:pl-healed', (event) => {
            const d = (event as CustomEvent).detail as { rootId?: unknown; value?: unknown } | null;
            const root = typeof d?.rootId === 'string' ? d.rootId.toUpperCase() : '';
            if (!root || typeof d?.value !== 'number' || !Number.isFinite(d.value)) return;
            for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-ft-pl-root]'))) {
                if ((el.dataset.ftPlRoot ?? '').toUpperCase() !== root) continue;
                el.outerHTML = d.value === 0 ? '' : renderPricedPlPill(d.value, el.dataset.ftPlItem ?? '');
            }
        });
    }
}
function itemName(arr: ObjectCardBlob): string { return String(itemsCol(arr, 'item_name') ?? ''); }
// An inventory INSTANCE — identity lives in the bucketed `inventory.inventory_id` on cache blobs
// (casterCache full-nest), with a flat `inventory_id` only on some server-enriched shapes. Check both.
function isInventory(arr: ObjectCardBlob): boolean {
    if (arr.inventory_id !== undefined) return true;
    return recordOf(arr.inventory)?.inventory_id !== undefined;
}

// computed.* direct reads — raw figures; formatting happens client-side (the server `_f`
// formatted twins are gone, Phase 4 of the leanup).
function computedPl(arr: ObjectCardBlob): unknown {
    const pl = recordOf(arr.computed)?.pl;
    if (pl == null) return undefined;
    // computed.pl is de-scalared to a bare number (2026-06-11); tolerate the legacy {0:n} too.
    if (Array.isArray(pl)) return pl[0];
    const legacy = recordOf(pl);
    return legacy ? legacy['0'] : pl;
}

// EquipmentVisualMount 'hidden' (additions.php). Objects mounted `hidden` are deliberately never
// drawn on the character, so the card suppresses the badge rather than stamping "not shown" on
// every potion and ration in the catalog.
const FT_MOUNT_HIDDEN_ID = '69E469E1-0DF4-4F9B-9B41-D7BB5CB70A33';

// Item types whose header icon links to item.php?id=<item_id> with icon_map(<item_id>)
// (the long elseif chain at table_item_object_inc.php:243-292).
const SIMPLE_ICON_TYPES = new Set<string>([
    'status_effect', 'damage_type', 'essence', 'innate_skill', 'gathering_skill', 'crafting_skill',
    'knowledge_skill', 'instrument_classification_skill', 'language_skill', 'language', 'weapon_skill',
    'armor_skill', 'defensive_skill', 'weather', 'temperature', 'tempurature_type', 'wind_type',
    'light_type', 'light_source', 'terrain_type',
    'environment', 'equipment_slot', 'circle_of_magic', 'sphere_of_magic', 'class_group', 'size_type',
    'creature_type', 'sense', 'speed_type', 'instrument_classification', 'armor_classification',
    'weapon_type', 'weapon_classification', 'weapon_ammo_classification', 'roll_type', 'skill_tier',
    'aoe_type', 'caster_type', 'poison_type', 'creature_role', 'range_type', 'season', 'subclass',
    'geographic_location_type', 'settlement_location_type', 'building_location_type', 'sun_state',
    'team_type', 'campaign_difficulty', 'campaign_house_rule_type', 'gender',
]);

// ── card picture (port of the render_carousel header, zero-DB) ───────────────────
// render_carousel() does DB work (videos, default art, classification carriers) we can't do from the
// cache. The compiled blob already carries its `pictures` bucket, so we pick the best picture_url from
// it and paint a single responsive <img> (the dominant single-image case). No blob picture → no image
// (the PHP default-art / video paths stay on the server render). Buckets in priority order; primary row
// (primary_picture==1) wins within a bucket.
const CARD_PICTURE_BUCKETS = [
    'item', 'inventory_thumbnail_picture', 'inventory_presentation_picture', 'inventory_picture',
    'encounter_creature_picture', 'player_character_picture',
    'weapon_classification', 'weapon_ammo_classification', 'armor_classification', 'instrument_classification',
];
function pictureUrlFromBuckets(pics: unknown): string {
    const buckets = recordOf(pics);
    if (!buckets) return '';
    const present = Object.keys(buckets).filter((k) => Array.isArray(buckets[k]) && buckets[k].length > 0);
    const ordered = [...CARD_PICTURE_BUCKETS.filter((k) => present.includes(k)),
        ...present.filter((k) => !CARD_PICTURE_BUCKETS.includes(k))];
    for (const k of ordered) {
        const list = Array.isArray(buckets[k]) ? buckets[k] : [];
        const primary = list.find((value) => Number(recordOf(value)?.primary_picture) === 1) ?? list[0];
        const picture = recordOf(primary);
        const url = picture?.picture_url ?? picture?.pic_url;
        if (url) return String(url);
    }
    return '';
}
// Classification-carrier picture fallback (get_thumbnail parity, owner-flagged 2026-06-10): an
// authored item with NO pictures of its own (e.g. the dragon's "Natural Armor +9") borrows its
// classification's art — the classification nests ride full on the cached blob with their own
// pictures buckets, so this stays zero-DB.
const CARD_PICTURE_CARRIER_LAYERS = [
    'armor_classification', 'weapon_classification', 'weapon_ammo_classification',
    'instrument_classification', 'armor_type',
];
// An inventory instance SELECTS its own display picture from the (shared) template gallery via
// inventory_thumbnail_pic_id / inventory_presentation_pic_id. Honor that specific row BEFORE the generic
// primary-wins pick below — otherwise a deduped/thinned instance (whose gallery lives in the root
// catalog) shows the template's base art instead of the per-instance thumbnail the user set. Mirrors
// the map token + the creature card's thumbnail/presentation selection. Scans the blob's own pictures
// buckets first, then the catalog-template bridge (picturesOf) for thinned instances.
function inventoryPictureUrlById(arr: ObjectCardBlob, wantId: string): string {
    if (!wantId) return '';
    const matchInList = (list: unknown): string => {
        if (!Array.isArray(list)) return '';
        const hit = recordOf(list.find((value) => String(recordOf(value)?.picture_id ?? '').toUpperCase() === wantId));
        const url = hit?.picture_url ?? hit?.pic_url;
        return url ? String(url) : '';
    };
    const pictures = recordOf(arr.pictures);
    if (pictures) {
        for (const k of Object.keys(pictures)) {
            const u = matchInList(pictures[k]);
            if (u) return u;
        }
    }
    for (const t of ['item', 'inventory_thumbnail_picture', 'inventory_presentation_picture', 'inventory_picture']) {
        const u = matchInList(picturesOf(arr, t));
        if (u) return u;
    }
    return '';
}
export function selectCardPictureUrl(arr: ObjectCardBlob): string {
    if (isInventory(arr)) {
        const wantId = String(
            inventoryField(arr, 'inventory_thumbnail_pic_id')
            ?? inventoryField(arr, 'inventory_presentation_pic_id')
            ?? '',
        ).toUpperCase();
        const chosen = inventoryPictureUrlById(arr, wantId);
        if (chosen !== '') return chosen;
    }
    const own = pictureUrlFromBuckets(arr.pictures);
    if (own !== '') return own;
    // Sideinfo/object API cards can carry a classification definition in their own catalog while the
    // ambient object cache is still cold. Resolve that local carrier first, just as the description
    // fallback does below; otherwise the inherited description renders but the same classification's
    // thumbnail is missing on first paint (for example, Javelin -> Javelins).
    const classification = classificationCarrierNode(arr);
    const classificationUrl = pictureUrlFromBuckets(classification?.pictures);
    if (classificationUrl !== '') return classificationUrl;
    const itemLayerTypes = recordOf(arr.item_layer_types);
    for (const layer of CARD_PICTURE_CARRIER_LAYERS) {
        const slot = itemLayerTypes?.[layer];
        const carriers = Array.isArray(slot) ? slot : (slot !== null && slot !== undefined ? [slot] : []);
        for (const c of carriers) {
            // The carrier may be a FULL nested object OR a bare-UUID ref (catalog-as-store: e.g. the
            // armor_classification on a natural-armor item is a bare UUID whose def + `pictures` live
            // in the ENCOUNTER-root `.catalog`, not nested here). Resolve the ref to its full node
            // before reading its pictures, else `c?.pictures` is undefined and the art never shows.
            let cnode = recordOf(c);
            const findFull = objectCardWindow().ftFindFullNestedItem;
            if (!cnode && typeof findFull === 'function') {
                cnode = recordOf(findFull(String(c)));
            }
            const u = pictureUrlFromBuckets(cnode?.pictures);
            if (u !== '') return u;
        }
    }
    // ROUND-4 thinned-instance fallback: an inventory instance whose template gallery / stamp
    // rows were dropped resolves them from the root-catalog template def via the shared
    // picturesOf bridge (which also walks the def's classification carriers).
    if (isInventory(arr)) {
        for (const t of ['item', 'inventory_thumbnail_picture', 'inventory_presentation_picture']) {
            const list = picturesOf(arr, t);
            if (list.length > 0) {
                const primary = list.find((p) => Number(p?.primary_picture) === 1) ?? list[0];
                const url = primary?.picture_url ?? primary?.pic_url;
                if (url) return String(url);
            }
        }
    }
    return '';
}
function renderCardPicture(arr: ObjectCardBlob): string {
    const url = selectCardPictureUrl(arr);
    if (!url) return '';
    const name = decodeHtmlEntitiesForPlainText(isInventory(arr)
        ? String(inventoryField(arr, 'inventory_name') ?? itemName(arr))
        : itemName(arr));
    // Size parity with the legacy carousel (ft_carousel_mount width:500 height:355 → build-carousel
    // max-height:355 / width:auto / object-fit:contain). The earlier w-100 + max-width:500px was
    // WIDTH-driven with no height cap, so a portrait image (e.g. Armor of War) painted 500px wide and
    // ran huge vertically. Cap BOTH axes (max 500×355), but keep the width bounded by the card so a
    // square classification image cannot overflow the narrow sideinfo drawer.
    return `<div class="text-center p-2"><img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" loading="lazy" decoding="async" `
        + `class="img-thumbnail img-fluid d-block mx-auto" style="max-width:min(100%,500px);max-height:355px;width:auto;object-fit:contain;"></div>`;
}

// The single-row "which picture is active" pointer buckets — an inventory instance's selected
// thumbnail/xz row. They are NOT galleries (they hold one row that also lives in the full gallery),
// so they must never be picked as the carousel's bucket; they only feed activePicId below.
const CARD_GALLERY_POINTER_BUCKETS = new Set<string>(['inventory_thumbnail_picture', 'inventory_presentation_picture']);

// Gallery bucket priority — mirrors ft_carousel_payload()'s picture-bucket cascade (one bucket, ALL
// its rows). An inventory instance prefers its OWN `inventory_picture` gallery over the template's
// `item` pictures (the PHP payload selects inventory_picture first, ft_carousel_mount_inc.php); a
// plain item/creature reads the `item` bucket. Kept separate from CARD_PICTURE_BUCKETS (the
// single-URL picker's order) because that list intentionally leads with the pointer buckets to pick
// a per-instance thumbnail, which is the opposite of what a gallery wants.
function cardGalleryBuckets(arr: ObjectCardBlob): string[] {
    return isInventory(arr)
        ? ['inventory_picture', 'item', 'user']
        : ['encounter_creature_picture', 'player_character_picture', 'item', 'user'];
}

// Ordered picture list from the highest-priority non-empty gallery bucket (primary-first) — the
// carousel GALLERY counterpart of pictureUrlFromBuckets / selectCardPictureUrl (which pick a single
// URL). Mirrors ft_carousel_payload()'s "one bucket, ALL its rows" selection so the client gallery
// picks the same set the server render did. Zero-DB: reads only the blob's own `pictures` buckets (the
// DB-fallback single-image cases — default art, classification carriers, thinned inventory — stay on
// renderCardPicture below).
function cardPictureList(arr: ObjectCardBlob): Record<string, unknown>[] {
    const buckets = recordOf(arr.pictures);
    if (!buckets) return [];
    const priority = cardGalleryBuckets(arr);
    const present = Object.keys(buckets).filter((k) =>
        !CARD_GALLERY_POINTER_BUCKETS.has(k) && Array.isArray(buckets[k]) && (buckets[k] as unknown[]).length > 0);
    const ordered = [...priority.filter((k) => present.includes(k)),
        ...present.filter((k) => !priority.includes(k))];
    for (const k of ordered) {
        const list = (Array.isArray(buckets[k]) ? buckets[k] : []) as unknown[];
        const nodes = list.map((v) => recordOf(v)).filter((v): v is Record<string, unknown> => v !== null);
        if (nodes.length === 0) continue;
        // primary_picture==1 opens the carousel; keep the rest in stored order (matches buildCarousel's
        // active-index + the legacy render_carousel primary-first ordering).
        const primaries = nodes.filter((n) => Number(recordOf(n)?.primary_picture ?? (Array.isArray(n.primary_picture) ? n.primary_picture[0] : n.primary_picture)) === 1);
        const rest = nodes.filter((n) => !primaries.includes(n));
        return [...primaries, ...rest];
    }
    return [];
}

// Card header media — VIDEO-over-picture priority (render_carousel parity, mirrors the creature /
// player cards): a blob carrying item_videos renders the canonical video carousel; otherwise the FULL
// picture carousel/gallery of every picture in the item's best bucket. Both ride via buildCarousel
// (hydrated by window.ftMountCarousels, which renderObjectCard / render-from-cache already run after
// paint). Only when the blob carries NEITHER own videos NOR own pictures does it fall through to the
// single-image render (classification-carrier art / default art / thinned-inventory catalog bridge).
// Media rides full on the cached blob (`item_videos` / `pictures`), so this stays zero-DB.
//
// Restores the multi-picture gallery the 2026-07-12 object-card TS conversion dropped: the archived
// PHP card mounted ft_carousel_mount() (a carousel of all pictures); the early TS port only painted a
// single picture (the "TODO Phase 2: carousel header still pending" gap in buildObjectCardHtml).
function renderCardMedia(arr: ObjectCardBlob): string {
    const videos = itemVideos(arr);
    const pics = cardPictureList(arr);
    // Only MULTIPLE pictures (or any video) build the carousel gallery — the case the conversion
    // actually dropped. A SINGLE picture (or none) stays on renderCardPicture so those cards render
    // byte-identically to today (bordered single image / classification-carrier / default art);
    // wrapping one image in carousel chrome would change the regular single-picture card for no gain.
    if (videos.length > 0 || pics.length > 1) {
        const name = decodeHtmlEntitiesForPlainText(isInventory(arr)
            ? String(inventoryField(arr, 'inventory_name') ?? itemName(arr))
            : itemName(arr));
        const domId = `oc_${String(isInventory(arr) ? (inventoryField(arr, 'inventory_id') ?? itemId(arr)) : itemId(arr))}`;
        // Inventory instance opens the gallery on its selected thumbnail/xz picture (parity with the
        // legacy carousel's activePicId); videos ignore it (buildCarousel opens videos at 0).
        const activePicId = isInventory(arr)
            ? (String(inventoryField(arr, 'inventory_thumbnail_pic_id') ?? inventoryField(arr, 'inventory_presentation_pic_id') ?? '') || null)
            : null;
        // Cap at 355 (not 500): parity with the single-image renderCardPicture that this replaced
        // (max-height:355) so restoring the gallery doesn't make the card taller than before.
        const carousel = buildCarousel({ domId, itemName: name, pics, videos, height: 355, activePicId });
        if (carousel !== '') return `<div class="text-center p-2">${carousel}</div>`;
    }
    return renderCardPicture(arr);
}

// ── header icon block (PHP 232-372) ─────────────────────────────────────────────
function renderHeaderIcon(arr: ObjectCardBlob): string {
    const type = itemType(arr);
    const id = itemId(arr);
    if (isInventory(arr) && arr.inventory_item_id !== undefined) {
        const invItemId = String(inventoryField(arr, 'inventory_item_id') ?? '');
        return `<a href="${itemHref(invItemId)}">${iconMap(type)}</a>`;
    }
    if (SIMPLE_ICON_TYPES.has(type)) {
        const iconKey = type === 'campaign_house_rule_type' ? 'campaign_house_rule_type' : id;
        return `<a href="${itemHref(id)}">${iconMap(iconKey)}</a>`;
    }
    if (type === 'spell' || type === 'curse') {
        const view = itemCardView(arr);
        const lvl = view.level;
        return `<span class="badge bg-primary">${escapeHtml(String(lvl ?? ''))}</span> | `
            + `<a href="${itemHref(id)}">${iconMap(view.levelIcon)}</a>`;
    }
    if (type === 'menta') {
        const view = itemCardView(arr);
        const lvl = view.level;
        return `<span class="badge bg-success">${escapeHtml(String(lvl ?? ''))}</span> | `
            + `<a href="${itemHref(id)}">${iconMap(view.levelIcon)}</a>`;
    }
    if (type === 'armor') {
        return `<a href="${itemHref(id)}">${iconMap(itemCardReferenceId(arr, 'armor_classification'))}</a>`;
    }
    // natural_weapon is a weapon too — show its weapon_classification icon in the header (parity with
    // table_item_object_inc.php and item.php), so the classification "rolls in" when a natural weapon
    // (e.g. the dragon's Bite/Claw) is opened in the side panel, not a generic fallback icon.
    if (type === 'weapon' || type === 'natural_weapon') {
        return `<a href="${itemHref(id)}">${iconMap(itemCardReferenceId(arr, 'weapon_classification'))}</a>`;
    }
    if (type === 'instrument') {
        return `<a href="${itemHref(id)}">${iconMap(itemCardReferenceId(arr, 'instrument_classification'))}</a>`;
    }
    if (type === 'creature_template') {
        let html = '';
        const roleSlot = layerOf(arr, 'creature_role');
        if (roleSlot !== null && roleSlot !== undefined) {
            const roleId = scalarLayerSlot(roleSlot);
            if (roleId !== '' && isUuid(roleId)) {
                html += `<span class="me-1" role="button" ${ocInfoAttrs(roleId)}>${iconMap(roleId)}</span>`;
            }
        }
        html += `<a href="${itemHref(id)}">${iconMap(type)}</a>`;
        return html;
    }
    // default
    return `<a href="${itemHref(id)}">${iconMap(type)}</a>`;
}

// ── 3D mesh presence (2026-08-19) ──────────────────────────────────────────────
// meshes_of() twin. assets['3d'] is the non-recursive presentation bucket build_item_arr.php
// compiles from mmdb.meshes (never an item layer), so it rides the blob like pictures do.
function meshRowsOf(arr: ObjectCardBlob): ObjectCardBlob[] {
    const bucket = recordOf(arr.assets);
    const rows = bucket ? bucket['3d'] : null;
    if (!Array.isArray(rows)) return [];
    const out: ObjectCardBlob[] = [];
    for (const row of rows) {
        const rec = recordOf(row);
        // `private` is CATALOG visibility, not a render exclusion — the encounter still draws these,
        // but the card must not count or list them. Same rule as the gallery endpoint's filter.
        if (rec && !rec.private) out.push(rec);
    }
    return out;
}

/**
 * "This item has 3D art" marker for the header cluster: the mesh icon, plus a count badge once
 * there is more than one attached mesh (11 colourway/body-profile GLBs on Clothes, Peasant).
 *
 * The classification FALLBACK (build_item_arr.php: an item with no mesh of its own inherits its
 * classification's, stamped inheritedFrom) is deliberately NOT counted: those meshes are not
 * connected to THIS item, so they get the icon at reduced emphasis and no number, which is the
 * honest reading of the badge. The two cases never mix — the fallback only fires when the item
 * has nothing of its own.
 *
 * The creature cards (render-creature-card.ts) carry the same marker, so a creature with its own
 * world mesh opens the gallery's turntable for a signed-in viewer too (owner 2026-10-19).
 */
export function renderMeshMarker(arr: ObjectCardBlob, names: ReferencedNames): string {
    const rows = meshRowsOf(arr);
    if (rows.length === 0) return '';

    // The marker opens the thumbnail gallery — the count answers "how many", the pictures answer
    // "which". Carries the name so the modal can title itself without a second lookup.
    const galleryAttrs = ocAttrs('open-mesh-gallery', {
        itemId: itemId(arr),
        itemName: String(decodeHtmlEntitiesForPlainText(itemName(arr)) ?? ''),
    });

    const inheritedFrom = String(rows[0]?.inheritedFrom ?? '');
    if (inheritedFrom !== '') {
        const src = nameOf(names, inheritedFrom);
        const label = !src || isUuid(src) ? 'its classification' : src;
        return `<span class="d-inline-block" role="button" style="opacity:.55" ${galleryAttrs}>`
            + `${iconMap('mesh', 22, 22, `3D mesh inherited from ${label} — click to preview`)}</span> `;
    }

    const variants = new Set(rows.map((row) => String(row.variant ?? '')).filter(Boolean)).size;
    const tip = rows.length === 1
        ? '1 3D mesh attached'
        : `${rows.length} 3D meshes attached`
            + (variants > 1 && variants !== rows.length ? ` (${variants} variants)` : '');
    const count = rows.length > 1
        ? `<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-dark text-light border border-light">${escapeHtml(String(rows.length))}<span class="visually-hidden"> attached 3D meshes</span></span>`
        : '';
    // me-2 on the wrapper, not just the cluster's gap-1: the count badge is translated half its own
    // width past the icon's right edge, and without the extra clearance it sat on top of the
    // item-type icon that follows it.
    const wrapClass = count === '' ? 'd-inline-block' : 'position-relative d-inline-block me-2';
    return `<span class="${wrapClass}" role="button" ${galleryAttrs}>${iconMap('mesh', 22, 22, tip + ' — click to preview')}${count}</span> `;
}

// ── name + header row (PHP 94-376) ───────────────────────────────────────────────
function renderNameRow(arr: ObjectCardBlob, names: ReferencedNames): string {
    const type = itemType(arr);
    let nameHtml = '';
    // qty badge
    if (Number(inventoryField(arr, 'item_qty') ?? 1) > 1) {
        nameHtml += `<span class="badge bg-dark border border-light text-light">${escapeHtml(String(inventoryField(arr, 'item_qty')))}</span> `;
    }
    // Names are stored HTML-entity-encoded in the DB (the PHP card echoes them raw so the browser
    // decodes). Decode FIRST, then escapeHtml — otherwise the entity's & is re-encoded and the literal
    // "&#039;" (etc.) shows on screen. Mirrors the creature-render / inventory bundle convention.
    const displayName = decodeHtmlEntitiesForPlainText(inventoryField(arr, 'inventory_name')
        ?? encounterCreatureField(arr.encounter_creatures, 'encounter_creature_name') ?? itemName(arr));
    nameHtml += escapeHtml(String(displayName ?? ''));
    const deleted = !!itemsCol(arr, 'item_deleted')
        || (isInventory(arr) && !!inventoryField(arr, 'inventory_deleted'));
    if (deleted) {
        nameHtml += ' <span class="ml-2 badge bg-danger">Deleted</span>';
    }

    // right-side header badges
    let right = '';
    const xp = posCoord(arr, 'x_pos');
    if (xp !== undefined && xp !== 0) {
        right += `<span class="badge bg-dark border">${iconMap('x_pos', 15, 15)} ${escapeHtml(String(xp))}</span> `;
    }
    const zp = posCoord(arr, 'z_pos');
    if (zp !== undefined && zp !== 0) {
        right += `<span class="badge bg-dark border">${iconMap('z_pos', 15, 15)} ${escapeHtml(String(zp))}</span> `;
    }
    if (isInventory(arr)) {
        const dc = Number(inventoryField(arr, 'inventory_inspection_dc') ?? 10);
        // GM eyes only (trap redaction, 2026-07-31): the flat Trap DC IS inventory_inspection_dc,
        // so a player-visible "DC 17" badge on a chest quietly advertises the trap-work the discern
        // gesture exists to earn. Players learn the DC by rolling against it, not by reading it.
        if (dc !== 10 && hasCampaignPermission(arr)) {
            right += `<span class="badge bg-dark border">${iconMap('inspection', 15, 15)} DC ${dc}</span> `;
        }
    }
    if (layerHas(arr, 'spell_concentration')) {
        right += iconMap('concentration') + ' ';
    }
    // SPELL_CASTING_TIME — PHP renders this whenever the layer is present (its type-exclusion
    // chain is an always-true `||` of `!=` checks, so it never actually gates). Mirror that here:
    // without it, abilities/spells with a long cast time (e.g. ten_minutes → "10 Minutes") lost
    // their cast-time badge in the side-info card vs item.php.
    if (layerHas(arr, 'spell_casting_time')) {
        right += displaySpellCastingTime(arr) + ' ';
    }
    if (isInventory(arr)) {
        right += `<a href="${itemHref(inventoryField(arr, 'inventory_id'))}" target="_blank">${iconMap('inventory')}</a> `;
    }
    right += renderMeshMarker(arr, names);
    right += renderHeaderIcon(arr);

    // Header row: title left, badge cluster right. The right side used to be a bare <h5> holding
    // mixed-height icons + the "10 Minutes" badge, which scattered/misaligned in the narrow side
    // panel. Use the house flex convention (render-creature-card.ts): a vertically-centered,
    // right-justified, wrapping cluster with a consistent gap. min-w-0 lets the (often long) title
    // shrink/wrap instead of shoving the badges off-edge; flex-shrink-0 keeps the cluster intact.
    const enchantmentControls = type === 'enchantment_template' && isUuid(itemId(arr))
        ? `<div class="mt-2 d-inline-flex align-items-center gap-1">
            <a class="btn btn-sm btn-primary d-inline-flex align-items-center justify-content-center" href="${escapeHtml(BASE_URL + 'tools/enchantment_templates/create_enchantment_template.php?from=' + encodeURIComponent(itemId(arr)))}" aria-label="Edit enchantment template">${iconMap('edit_enchantment_template')}</a>
            <button class="btn btn-sm btn-success" ${ocAttrs('copy-enchantment-template', { itemId: itemId(arr) })}>${iconMap('copy_enchantment_template')}</button>
            <input type="hidden" id="input_hidden_enchantment_template_id_${escapeHtml(itemId(arr))}" value="${escapeHtml(itemId(arr))}">
            <div class="fw-bold" id="div_refresh_${escapeHtml(itemId(arr))}"></div>
        </div>`
        : '';
    return `
    <div class="card-title">
        <div class="d-flex align-items-center">
            <div class="flex-grow-1 h5 mb-0 min-w-0">${nameHtml}</div>
            <div class="d-flex align-items-center flex-wrap justify-content-end gap-1 ms-2 flex-shrink-0">${right}</div>
        </div>
        ${renderObjectTags(arr, names)}
        ${renderRequirements(arr, names)}
        ${enchantmentControls}
    </div>
    <hr />`;
}

// ── object tags (PHP 380-674) ────────────────────────────────────────────────────
function renderObjectTags(arr: ObjectCardBlob, names: ReferencedNames): string {
    const type = itemType(arr);
    const view = itemCardView(arr, names, inheritedLayer, id => nameOf(names, id));
    let tags = '';

    // size (not for creature_template / creature_sub_type family)
    // ⛔ EFFECTIVE size: a per-instance `inventory_size` OUTRANKS the item's own size layer.
    //    Reading the layer alone showed every collapsed door/window at its BASE size (a Large stone
    //    door tagged Medium) once the 2026-09-04 size-variant collapse moved the real size onto the
    //    row. NULL = inherit, so catalog items and un-overridden instances are unchanged.
    //    Composed from the module's own helpers rather than the ftEffectiveSizeId global, to match
    //    how everything else in this file reads.
    const sizeId = view.sizeId;
    if (sizeId && type !== 'creature_template' && !isCreatureSubTypeItemType(type)) {
        tags += `<div class="pr-1"><span role="button" ${ocInfoAttrs(String(sizeId))}>${iconMap(String(sizeId))}</span></div>`;
    }
    // creature_type (on a creature_sub_type-family item)
    if (isCreatureSubTypeItemType(type) && layerOf(arr, 'creature_type')) {
        const ct = itemCardReferenceId(arr, 'creature_type');
        tags += `<div class="pr-1"><span role="button" ${ocInfoAttrs(ct)}>${iconMap(ct)}</span></div>`;
    }

    // type label + small flags
    let mid = `<span class="fst-italic fw-light ft-object-type-label">${escapeHtml(ucwordsAndSpace(type))}</span>`;
    if (view.repeatable) mid += ' ' + iconMap('feat_repeatable');
    if (view.equipmentSlot) mid += ' ' + iconMap(view.equipmentSlot);
    if (view.consumesUnitPerUse) mid += ' ' + iconMap('consumes_unit_per_use');
    // equipment_visual_mount (2026-08-17) sits immediately after the gameplay slot, because the
    // two answer adjacent questions: "which slot does this use" then "where does it sit on the
    // body". Its art is a deliberately distinct `mount_*` composite — bare `waist`/`back` are the
    // SLOT glyphs, and an identical icon twice in a row would read as a duplicate rather than as
    // a second fact.
    //
    // Suppressed for `hidden`: most objects are never drawn on the character (potions, rations,
    // coins), and a "not shown" badge on every one of them is noise. This row is here to say
    // where a WEAPON or SHIELD rides.
    if (layerHas(arr, 'equipment_visual_mount')) {
        const mountId = String(layerOf(arr, 'equipment_visual_mount') ?? '');
        if (mountId && mountId.toUpperCase() !== FT_MOUNT_HIDDEN_ID) {
            mid += ' ' + iconMap(mountId);
        }
    }

    // right cluster: consumable / unidentified flags + cost + weight + composition + PL
    let rightCluster = '';
    if (layerHas(arr, 'consumable')) rightCluster += iconMap('consumable', 15, 15) + ' ';
    if (isInventory(arr) && inventoryField(arr, 'inventory_unidentified')) rightCluster += iconMap('unidentified') + ' ';

    // cost badge — prefer computed.cost.total_gp, then cost_gp / enchantment_total layers.
    // 0 gp = priceless (the icon, mirroring the PHP icon_ph('priceless') branch).
    const costGp = view.costGp;
    if (costGp !== undefined && costGp !== null && costGp !== '') {
        const inner = view.priceless ? iconMap('priceless') : escapeHtml(makeNumberPretty(costGp));
        rightCluster += `<span class="badge bg-warning text-dark">${inner} gp</span> `;
    }

    // weight badge — computed gross, then total, then weight layer
    const gross = view.grossWeight;
    if (gross !== undefined && gross !== null) {
        rightCluster += `<span class="badge bg-primary">${escapeHtml(makeNumberPretty(gross))} lb${Number(gross) > 1 ? 's' : ''}</span> `;
    }

    // item_composition (nested entry or UUID)
    const comp = inheritedLayer(arr, 'item_composition');
    if (comp !== null && comp !== undefined) {
        let compId = '';
        let compName = '';
        if (typeof comp === 'object') {
            compId = String(itemsCol(comp, 'item_id') ?? '');
            compName = String(itemsCol(comp, 'item_name') ?? '') || nameOf(names, compId);
        } else {
            compId = String(comp);
            compName = nameOf(names, compId);
        }
        if (compId !== '') {
            rightCluster += `<a href="${itemHref(compId)}"><span class="badge bg-secondary">${escapeHtml(compName)}</span></a> `;
        }
    }

    // Cast preview (spell-vfx-marker.ts) sits just left of the PL pill; '' when signed out or no effect.
    rightCluster += renderSpellVfxMarker(arr);

    // PL — host computed.pl[0], else walk default-provider carriers
    let pl = computedPl(arr);
    if (pl === undefined || pl === null || pl === '' || pl === 0) {
        for (const carrier of resolvedDefaultProviderCarriers(arr)) {
            const cpl = computedPl(carrier);
            if (cpl !== undefined && cpl !== null && cpl !== '' && cpl !== 0) { pl = cpl; break; }
        }
    }
    // C2b step 2 (plan §9 amendment 14): an unpriced row whose compile marked it pending shows a dash. It names its heal root (an
    // instance's inventory id, else the item id) so the page's healer can swap in the priced pill once it saves (ft:pl-healed below).
    const instanceRoot = plInstanceRootOf(arr);
    if (pl === undefined || pl === null) {
        const pending = !!recordOf(arr.computed)?.pl_pending;
        rightCluster += `<span class="d-inline-block" data-bs-toggle="tooltip" data-bs-original-title="Power Level: ${pending ? 'pending' : 'not priced yet'}" data-ft-pl-root="${escapeHtml(plRootIdOf(arr))}" data-ft-pl-item="${escapeHtml(itemId(arr))}"${instanceRoot ? ' data-ft-pl-instance="1"' : ''}><span class="badge bg-light text-muted ft-pl-pending">—</span></span> `;
    }
    if (pl !== undefined && pl !== null && pl !== '' && pl !== 0) {
        rightCluster += renderPricedPlPill(pl, itemId(arr), instanceRoot);
    }
    if (instanceRoot) schedulePlInstanceResolve();

    return `
        <div class="d-flex">
            ${tags}
            <div class="flex-grow-1">${mid}</div>
            <div>${rightCluster}</div>
        </div>`;
}

// ── requirements: ancestry / lineage / feat / class / subclass / level / skill tier (PHP 677-918) ──
function renderRequirements(arr: ObjectCardBlob, names: ReferencedNames): string {
    let html = '';
    const view = itemCardView(arr, names, inheritedLayer, id => nameOf(names, id));

    const badgeList = (layer: string, withIcon = false) => {
        if (!layerHas(arr, layer)) return '';
        let out = '';
        for (const entry of view.requirements.filter(entry => entry.layer === layer)) {
            const { id, name } = entry;
            const inner = withIcon
                ? `<span class="pr-2 d-inline-flex align-items-center">${iconMap('requires_feat', 15, 15)}</span>${escapeHtml(name)}`
                : escapeHtml(name);
            out += `<span class="badge bg-primary${withIcon ? ' d-inline-flex align-items-center' : ''}" role="button" ${ocInfoAttrs(id)}>${inner}</span> `;
        }
        return out;
    };

    html += badgeList('requires_ancestry');
    html += badgeList('requires_lineage');
    html += badgeList('requires_feat', true);

    // class / subclass / level block
    if (layerHas(arr, 'requires_class') || layerHas(arr, 'requires_level')
        || layerHas(arr, 'requires_subclass') || layerHas(arr, 'requires_class_level')) {
        let block = '<div class="mt-2">';
        if (layerHas(arr, 'requires_level')) {
            block += `<span class="badge bg-info">${escapeHtml(String(view.requiredLevel ?? ''))}</span>`;
        }
        const classLevelBadge = layerHas(arr, 'requires_class_level')
            ? `<span class="position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success">${escapeHtml(String(view.requiredClassLevel ?? ''))}</span>`
            : '';
        const classIcons = (layer: string) => {
            let out = '';
            for (const entry of view.requirements.filter(entry => entry.layer === layer)) {
                out += `<a href="${itemHref(entry.id)}" target="_blank" class="position-relative">${classLevelBadge}${iconMap(entry.id, 40, 40)} ${escapeHtml(entry.name)}</a> `;
            }
            return out;
        };
        if (layerHas(arr, 'requires_subclass')) {
            block += classIcons('requires_subclass');
        } else if (layerHas(arr, 'requires_class')) {
            block += classIcons('requires_class');
        }
        block += '</div>';
        html += block;
    }

    // Skill-tier gates are authored as dynamic requires_skill_tier_N layers rather
    // than one fixed layer. Keep the tier icon and required-skill icon together in
    // the warning badge used by the legacy card header.
    const skillTierKeys = new Set(Array.from({ length: 25 }, (_, tier) => `requires_skill_tier_${tier}`));
    for (const key of [...Object.keys(arr), ...Object.keys(recordOf(arr.item_layer_types) ?? {})]) {
        if (/^requires_skill_tier_\d+$/.test(key)) skillTierKeys.add(key);
    }
    let skillTierBadges = '';
    for (const tierKey of [...skillTierKeys].sort((a, b) => Number(a.slice(20)) - Number(b.slice(20)))) {
        if (!layerHas(arr, tierKey)) continue;
        for (const entry of layerEntriesOf(arr, tierKey)) {
            const skillId = scalarLayerSlot(entry) || layerListEntryUuid(entry) || String(entry ?? '');
            if (!skillId) continue;
            skillTierBadges += `<span class="badge bg-warning">`
                + `<span role="button" ${ocInfoAttrs('02697138-9C12-4B29-ACD0-1DEB6258D849')}>${iconMap(tierKey)}</span> `
                + `<span role="button" ${ocInfoAttrs(skillId)}>${iconMap(skillId)}</span>`
                + `</span> `;
        }
    }
    if (skillTierBadges) html += `<div class="mt-2">${skillTierBadges}</div>`;

    return html;
}

// Classification description / flavor-text default-provider fallback. item_description and
// flavor_text live in the items bucket (NOT as layer values), so the layerOf carrier walk never
// reaches them. A description-less item (e.g. generic ammo whose "Great Arrows"
// weapon_ammo_classification carries the text) defaults to its classification carrier's text —
// just as weight / cost_gp / item_composition already flow from the classification. Scoped to the
// *_classification carriers ONLY (not creature_template / PC providers, whose prose must not bleed
// onto description-less creatures/characters). Twin of ft_item_description_default() /
// ft_flavor_text_default() in build_item_arr.php.
const DESCRIPTION_DEFAULT_CARRIER_LAYERS = [
    'weapon_classification', 'weapon_ammo_classification', 'armor_classification', 'instrument_classification',
];
function classificationCarrierNode(arr: ObjectCardBlob): ObjectCardBlob | null {
    const itemLayerTypes = recordOf(arr.item_layer_types);
    if (!itemLayerTypes) return null;
    const findFull = (id: string): ObjectCardBlob | null => {
        const catalog = recordOf(arr.catalog);
        if (catalog) {
            const direct = recordOf(catalog[id]) ?? recordOf(catalog[id.toUpperCase()]) ?? recordOf(catalog[id.toLowerCase()]);
            if (direct) return direct;
            for (const value of Object.values(catalog)) {
                const candidate = recordOf(value);
                if (candidate && String(itemsCol(candidate, 'item_id') ?? '').toUpperCase() === id.toUpperCase()) return candidate;
            }
        }
        const finder = objectCardWindow().ftFindFullNestedItem;
        return typeof finder === 'function' ? recordOf(finder(id)) : null;
    };
    for (const name of DESCRIPTION_DEFAULT_CARRIER_LAYERS) {
        const slot = itemLayerTypes[name];
        if (typeof slot === 'string' && isUuid(slot)) {
            const node = findFull(slot);
            if (node) return node;
            continue;
        }
        if (!slot || typeof slot !== 'object') continue;
        // Single-valued slot IS the carrier arr; multi-valued list → first entry.
        const entry = Array.isArray(slot) ? slot[0] : slot;
        const node = recordOf(entry);
        if (node) return node;
        if (typeof entry === 'string' && isUuid(entry)) {
            const full = findFull(entry);
            if (full) return full;
        }
    }
    return null;
}
function withClassificationDefault(arr: ObjectCardBlob, key: string): string {
    const own = String(itemsCol(arr, key) ?? '');
    if (own.trim() !== '') return own;
    const carrier = classificationCarrierNode(arr);
    if (carrier) {
        const v = String(itemsCol(carrier, key) ?? '');
        if (v.trim() !== '') return v;
    }
    return own;
}

// ── description + flavor + environment (PHP 1978-2247) ───────────────────────────
function renderDescription(arr: ObjectCardBlob): string {
    // NOTE: written-language translate + inventory_gm_notes are Phase 2 islands /
    // enrichment-gated. Phase 1 renders the plain description + flavor text.
    //
    // Inventory instances DEFAULT to the item TEMPLATE's description/flavor when their own
    // inventory_description / inventory_flavor_text doesn't override it (empty or absent). This mirrors
    // build_inventory_arr.php (inventory_description = inventory override ?? item_description) and the
    // table_item_object_inc.php flavor render. The stored bucket value is often an EMPTY STRING (the DB
    // column is "" rather than NULL, so the build's layer_has() NULL-fallback never fires), so reading
    // it alone makes an instantly-pulled inventory lose the template text — hence the empty-string check.
    let descRaw = '';
    let domId = '';
    if (isInventory(arr)) {
        const invDesc = String(inventoryField(arr, 'inventory_description') ?? '');
        descRaw = invDesc !== '' ? invDesc : withClassificationDefault(arr, 'item_description');
        domId = String(inventoryField(arr, 'inventory_id') ?? itemId(arr));
    } else {
        descRaw = withClassificationDefault(arr, 'item_description');
        domId = itemId(arr);
    }
    let html = '';
    const writtenLanguage = isInventory(arr)
        ? String(inventoryField(arr, 'inventory_written_in_language') ?? '')
        : String(layerOf(arr, 'written_in_language') ?? '');
    // Static-display projection (synthetic inventory_id, no DB row): every inventory-ID-backed island
    // would POST a nonexistent id → suppress them all and render the cached text directly. The
    // written-language gate is a live-instance mechanic (who can read THIS copy) that doesn't apply to
    // a statblock's reference projection.
    const staticDisplay = isStaticDisplayInventory(arr);
    if (writtenLanguage !== '' && !staticDisplay) {
        html += islandPlaceholder('written_description', String(inventoryField(arr, 'inventory_id') ?? itemId(arr)));
    } else if (descRaw !== '') {
        // parse_text() port (shared/parse-text.ts) — matches the PHP <?= parse_text($desc) ?> render.
        // [table:]/[random_table:] tags expand to lazy mounts; ftMountParsedText hydrates them.
        html += `<div id="div_item_description_${escapeHtml(domId)}" class="card-text">${parseText(descRaw)}</div>`;
    }
    // flavor — same inventory-override-else-template fallback as the description above.
    let flavor: string;
    if (isInventory(arr)) {
        const invFlavor = String(inventoryField(arr, 'inventory_flavor_text') ?? '');
        flavor = invFlavor !== '' ? invFlavor : withClassificationDefault(arr, 'flavor_text');
    } else {
        flavor = withClassificationDefault(arr, 'flavor_text');
    }
    if (flavor !== '') {
        html += `<div id="div_item_flavor_text_${escapeHtml(itemId(arr))}" class="card-text border border border-2 rounded p-3 m-3">${parseText(flavor)}</div>`;
    }
    if (isInventory(arr) && !staticDisplay && String(inventoryField(arr, 'inventory_gm_notes') ?? '') !== '') {
        html += islandPlaceholder('gm_notes', String(inventoryField(arr, 'inventory_id') ?? ''));
    }
    const type = itemType(arr);
    if (['table', 'random_table', 'random_encounter_table'].includes(type)) {
        html += islandPlaceholder('admin_controls', itemId(arr));
    }
    if (['table', 'random_table', 'random_encounter_table', 'random_loot_table', 'random_name_table'].includes(type)) {
        html += islandPlaceholder('table_body', itemId(arr));
    }
    return html;
}

function renderEnvironment(arr: ObjectCardBlob): string {
    if (!layerHas(arr, 'creature_environment')) return '';
    const entries = layerEntriesOf(arr, 'creature_environment');
    let html = `<p class="card-text"><span class="fw-bold">Environment${entries.length > 1 ? 's' : ''}:</span> `;
    for (const e of entries) {
        const id = scalarLayerSlot(e) || layerListEntryUuid(e) || String(e ?? '');
        html += `<span class="p-1" role="button" ${ocInfoAttrs(id)}>${iconMap(id)}</span>`;
    }
    return html + '</p>';
}

// ── source footer (PHP 2251-2263) ────────────────────────────────────────────────
function renderSourceFooter(arr: ObjectCardBlob): string {
    return sourceFooterHtml(arr);
}

// ── property-list delegation (the already-ported list-display bundle) ────────────
function renderPropertyList(arr: ObjectCardBlob, names: ReferencedNames): string {
    if (recordOf(recordOf(arr._ft_object_card)?.ctx)?.form_mode) return '';
    // The list-display bundle renders into a container; we can't call it inside a
    // string build, so we emit a placeholder mount and hydrate after insertion (see
    // index.ts, which calls window.renderItemListDisplay into this node).
    // mt-4: the "Object Properties" wrap (list-display-object-properties-wrap) carries
    // only mb-2, so without this it sits flush against the description text above.
    return '<div class="ft-oc-property-list mt-4" data-oc-prop-list="1"></div>';
}

// ── inventory interactive controls (PHP 1037-2024, blob-computable subset) ───────
// Port of the player-facing INVENTORY CONTROLS + consume + container-contents region of
// table_item_object_inc.php. Cache-first cards use held state for book/gather chrome and
// narrow islands for remaining saved facts. Turn-to-rubble is shown optimistically here;
// the browser destruction lane decides it or shows a named refusal.
// Handlers (openEditInventoryForm / openInventoryStatusEffects /
// queryData / useAbilityVariable1 / closeSideInfoPanel / openSideLog) are template.js globals invoked
// from the emitted onclick strings, exactly as the PHP emits them.

function targetStatusCount(arr: ObjectCardBlob): number {
    const s = recordOf(arr.statuses)?.target;
    if (Array.isArray(s)) return s.length;
    if (s !== null && typeof s === 'object') return Object.keys(s).length;
    return 0;
}

// is_inventory_arr_a_container() port — explicit container type or any carrying_capacity_* marker.
export function isContainerArr(arr: ObjectCardBlob): boolean {
    if (itemType(arr) === 'container') return true;
    return !!arr && (
        arr.carrying_capacity_pounds !== undefined
        || arr.carrying_capacity_volume !== undefined
        || layerHas(arr, 'carrying_capacity_pounds')
        || layerHas(arr, 'carrying_capacity_volume')
    );
}

// ft_inventory_tile_mount() port — the JSON-in-a-script tile mount window.ftMountInventoryTiles
// hydrates (renderObjectCard already invokes that hydrator after painting). Options mirror the PHP
// container-contents call site, PLUS flow_layout:true — ft_inventory_tile_mount() sets that for every
// off-map mount, and without it renderInventoryThumbnail defaults to the XZ map's position:absolute, so
// the tiles stack at the panel's top-left and overflow/overlap instead of flowing in a grid.
export function inventoryTileMount(inv: ObjectCardBlob, size = 70): string {
    const payload = {
        inventory: inv,
        options: {
            show_overlay: false,
            use_size_based_width: false,
            thumbnail_width: size,
            thumbnail_height: size,
            is_draggable: true,
            show_log_message: 1,
            show_icons: false,
            flow_layout: true,
            additional_classes: ['m-1'],
        },
    };
    const json = JSON.stringify(payload).replace(/<\//g, '<\\/');
    return `<div class="ft-inv-tile-mount"><script type="application/json" class="ft-inv-tile-blob">${json}</script></div>`;
}

// "Blocked out" contents — the viewer is out of reach (non-GM / GM-through-creature). We render NO item
// tiles and NO contents-derived figures: inventoryTileMount() embeds the FULL inventory object as JSON in
// the card DOM (+ hardcodes is_draggable), so blurring the real tiles would still leak every name / id /
// qty and could still hydrate draggable descendants (Codex 2026-07-12). Instead paint a self-contained
// frosted placeholder with no inventory data and no dropzone — nothing to inspect, nothing to drag out.
// (Coarse aggregates like the contents GP total / held weight also leak, so they're dropped here too;
// the container's own capacity is a container property and could be shown, but we keep it clean.)
function renderContainerContentsBlocked(): string {
    return `
    <div class="position-relative mt-2 mb-0 pt-2 pb-2 px-2 bg-info text-dark bg-gradient rounded-top">
        <div class="d-flex flex-wrap align-items-center gap-2">
            <h5 class="mb-0 text-left d-inline-flex align-items-center gap-2">${iconMap('container', 22, 22)}Container Inventory</h5>
        </div>
    </div>
    <div class="d-flex flex-column justify-content-center align-items-center text-center border rounded-bottom p-4 text-muted" style="min-height:80px; gap:0.35rem;">
        <div class="d-inline-flex align-items-center" style="filter:blur(1.5px); opacity:0.7;">${iconMap('hidden', 34, 34)}</div>
        <div class="fw-semibold">Contents out of reach</div>
        <div class="small">Move closer to inspect what is inside.</div>
    </div>`;
}

// Buried digsite contents — same no-leak rationale as renderContainerContentsBlocked() (the tile
// mount would embed the full nested JSON), but with excavation-flavored copy: the viewer may know
// SOMETHING is buried (the digsite itself was revealed by inspection) without learning what.
function renderContainerContentsBuried(): string {
    return `
    <div class="position-relative mt-2 mb-0 pt-2 pb-2 px-2 bg-info text-dark bg-gradient rounded-top">
        <div class="d-flex flex-wrap align-items-center gap-2">
            <h5 class="mb-0 text-left d-inline-flex align-items-center gap-2">${iconMap('digsite', 22, 22)}Digsite</h5>
        </div>
    </div>
    <div class="d-flex flex-column justify-content-center align-items-center text-center border rounded-bottom p-4 text-muted" style="min-height:80px; gap:0.35rem;">
        <div class="d-inline-flex align-items-center" style="filter:blur(1.5px); opacity:0.7;">${iconMap('hidden', 34, 34)}</div>
        <div class="fw-semibold">Contents buried</div>
        <div class="small">Excavate the site to unearth whatever lies inside.</div>
    </div>`;
}

// ── Container contents sort (A–Z / value) ──────────────────────────────────────
// The choice is a per-viewer convenience (localStorage, try/catch — private windows throw), applied at
// render time so every repaint keeps it; the toggle also reorders the live DOM so tiles already
// hydrated (and their drag bindings) survive without a card re-render.
type ContainerSortMode = 'alpha' | 'value';
const CONTAINER_SORT_KEY = 'ft.containerContentsSort';

function containerSortMode(): ContainerSortMode {
    try { return localStorage.getItem(CONTAINER_SORT_KEY) === 'value' ? 'value' : 'alpha'; } catch { return 'alpha'; }
}

function containerRowName(row: ObjectCardBlob): string {
    return decodeHtmlEntitiesForPlainText(String(inventoryField(row, 'inventory_name') || itemName(row) || ''));
}

// Stack value = computed.cost.total_gp (qty × unit), plus a nested container's contents.
function containerRowValue(row: ObjectCardBlob): number {
    return inventoryRowTotals(row).totalGp;
}

function sortContainerRows(rows: ObjectCardBlob[], mode: ContainerSortMode): ObjectCardBlob[] {
    const byName = (a: ObjectCardBlob, b: ObjectCardBlob) =>
        containerRowName(a).localeCompare(containerRowName(b), undefined, { sensitivity: 'base', numeric: true });
    return [...rows].sort(mode === 'value'
        ? (a, b) => (containerRowValue(b) - containerRowValue(a)) || byName(a, b)
        : byName);
}

// A child of the contents grid is either a hydrated tile (<div id="inventory_…">) or a not-yet-hydrated
// mount still holding its JSON blob — resolve the inventory_id for both so a sort never strands a mount.
function containerChildInventoryId(el: Element): string {
    const blob = el.querySelector(':scope > script.ft-inv-tile-blob');
    if (blob) {
        try { return String(inventoryField(recordOf(recordOf(JSON.parse(blob.textContent || '{}'))?.inventory) ?? {}, 'inventory_id') ?? ''); } catch { return ''; }
    }
    return el.id.startsWith('inventory_') ? el.id.slice('inventory_'.length) : '';
}

document.addEventListener('click', event => {
    const btn = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ft-container-sort]') : null;
    if (!btn) return;
    event.preventDefault();
    const mode: ContainerSortMode = btn.dataset.ftContainerSort === 'value' ? 'value' : 'alpha';
    try { localStorage.setItem(CONTAINER_SORT_KEY, mode); } catch { /* per-viewer convenience only */ }
    btn.parentElement?.querySelectorAll<HTMLElement>('[data-ft-container-sort]').forEach((b) => {
        const on = b.dataset.ftContainerSort === mode;
        b.classList.toggle('btn-dark', on);
        b.classList.toggle('btn-outline-dark', !on);
        b.setAttribute('aria-pressed', String(on));
    });
    const grid = btn.closest('[data-ft-world-container]')?.querySelector('[data-ft-container-grid]')
        ?? btn.closest('.bg-info')?.nextElementSibling;
    if (!(grid instanceof HTMLElement) || (grid.id !== 'div_caster_view_container_inventory' && !grid.hasAttribute('data-ft-container-grid'))) return;
    let keys: Record<string, [string, number]> = {};
    try { keys = JSON.parse(grid.dataset.ftSortKeys || '{}'); } catch { return; }
    const children = Array.from(grid.children).map((el) => ({ el, key: keys[containerChildInventoryId(el)] }));
    const keyedChildren = children.filter((child): child is { el: Element; key: [string, number] } => child.key !== undefined);
    if (keyedChildren.length !== children.length) return; // unknown child (empty-state, foreign node) — leave the order alone
    const byName = (a: typeof keyedChildren[number], b: typeof keyedChildren[number]) =>
        a.key[0].localeCompare(b.key[0], undefined, { sensitivity: 'base', numeric: true });
    keyedChildren.sort(mode === 'value' ? (a, b) => (b.key[1] - a.key[1]) || byName(a, b) : byName);
    for (const c of keyedChildren) grid.appendChild(c.el);
});

function renderContainerContents(arr: ObjectCardBlob, invIdEsc: string, blocked = false): string {
    if (blocked) return renderContainerContentsBlocked();

    const totals = inventoryRowTotals(arr);
    const gross = makeNumberPretty(totals.grossLb);
    const held = makeNumberPretty(totals.heldLb);
    const cap = totals.capacityLb ?? '';
    const contentsGp = makeNumberPretty(totals.contentsGp);

    const rawEntries = arr.inventory_in_container;
    const entries = Array.isArray(rawEntries) ? rawEntries : (rawEntries ? [rawEntries] : []);
    const rows = entries.map((entry) => recordOf(entry)).filter((entry): entry is ObjectCardBlob => entry !== null);
    const mode = containerSortMode();
    const tiles = sortContainerRows(rows, mode).map((entry) => inventoryTileMount(entry)).join('');
    const sortBtn = (m: ContainerSortMode, label: string, title: string) =>
        `<button type="button" class="btn btn-sm ${m === mode ? 'btn-dark' : 'btn-outline-dark'} py-0 px-2" data-ft-container-sort="${m}" title="${title}" aria-pressed="${m === mode}">${label}</button>`;
    const sortToggle = rows.length > 1
        ? `<span class="btn-group user-select-none" role="group" aria-label="Sort contents">${sortBtn('alpha', 'A–Z', 'Sort by name')}${sortBtn('value', iconMap('cost_gp', 14, 14) + ' Value', 'Sort by value (highest first)')}</span>`
        : '';
    const sortKeys: Record<string, [string, number]> = {};
    for (const row of rows) sortKeys[String(inventoryField(row, 'inventory_id') ?? '')] = [containerRowName(row), containerRowValue(row)];

    // Stat-strip chip styling = single-source global .ft-container-stats-strip (css/style.css,
    // source css/src/95-pc-sheet.css). No inline <style> here — keep it identical everywhere.
    return `
    <div class="position-relative mt-2 mb-0 pt-2 pb-2 px-2 bg-info text-dark bg-gradient rounded-top">
        <div class="d-flex flex-wrap align-items-center gap-2">
            <h5 class="mb-0 text-left d-inline-flex align-items-center gap-2">${iconMap('container', 22, 22)}Container Inventory</h5>
            <div class="d-flex flex-wrap gap-1 align-items-center justify-content-end ms-auto">
                ${sortToggle}
                <span class="ft-container-stats-strip user-select-none py-1 px-2 rounded">
                    <span class="d-inline-flex align-items-center gap-1">${iconMap('sum_weight', 18, 18)}<span class="badge bg-light text-dark border border-dark">${escapeHtml(String(gross))}</span></span>
                    <span class="ft-container-stats-strip__sep">&middot;</span>
                    <span class="d-inline-flex align-items-center gap-1">${iconMap('weight', 18, 18)}<span class="badge bg-light text-dark border border-dark">${escapeHtml(String(held))}</span><span class="text-muted">/</span>${iconMap('carrying_capacity_pounds', 18, 18)}<span class="badge bg-light text-dark border border-dark">${escapeHtml(String(cap))}</span></span>
                </span>
                <span class="ft-container-stats-strip user-select-none py-1 px-2 rounded" style="white-space:nowrap;">${iconMap('cost_gp', 18, 18)}<span class="badge bg-warning text-dark border border-dark">${escapeHtml(String(contentsGp))}</span></span>
            </div>
        </div>
    </div>
    <div
        id="div_caster_view_container_inventory"
        class="d-flex justify-content-center flex-wrap border rounded-bottom dropzone p-2"
        data-dropzone_type='dropzone_move_inventory'
        data-dropzone_id="${invIdEsc}"
        data-ft-sort-keys="${escapeHtml(JSON.stringify(sortKeys))}"
    >
        ${tiles || '<div class="mt-3 mb-1" style="min-height:50px;"><h5>No Inventory in Container</h5></div>'}
    </div>`;
}

// A static item-creature's equipped-gear projection (enchantment_and_inventory_layering.php) carries a
// SYNTHETIC inventory_id that exists in no DB row, so it must render fully READ-ONLY — no mutation
// controls, no consume/disenchant/book actions (all would POST/island a nonexistent id). Detected off
// the server's inventory_static_display marker (not merely absent permissions).
function isStaticDisplayInventory(arr: ObjectCardBlob): boolean {
    const v: unknown = inventoryField(arr, 'inventory_static_display');
    return v === 1 || v === '1' || (v as unknown) === true;
}

export function inventoryUsesSceneRadial(arr: ObjectCardBlob): boolean {
    const host = window as Window & { ftGetActiveEncounterId?: () => string };
    return !!document.querySelector('[data-ft3d-encounter-frame]')
        && String(inventoryField(arr, 'possess_id') ?? '').toUpperCase() === String(host.ftGetActiveEncounterId?.() ?? '').toUpperCase();
}

export function renderInventoryControls(arr: ObjectCardBlob, radialOnly = false): string {
    if (encounterPreviewSurface() && !encounterAuthoringActive()) return '';
    if (!isInventory(arr)) return '';
    if (isStaticDisplayInventory(arr)) return ''; // read-only projection — no controls
    const type = itemType(arr);
    const invId = String(inventoryField(arr, 'inventory_id') ?? '');
    const invIdEsc = escapeHtml(invId);

    // Page-scope context the PHP partial pulls from $campaign_arr / $caster_id — supplied by the sidelog.
    const sc = (typeof getSidelogContext === 'function' ? (getSidelogContext() || {}) : {}) as Record<string, unknown>;
    const campEsc = escapeHtml(String(sc.campaign_id ?? ''));
    const encEsc = escapeHtml(String(sc.encounter_id ?? ''));
    // sidelog.caster_id was retired as a source of truth in favor of FateTide.activeCasterId
    // (ftGetActiveCasterId). Keeping the old context read as the only source made this value blank
    // on campaign pages and silently suppressed every caster-scoped inventory action added here.
    const activeCasterGetter = (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId;
    const activeCasterId = String(
        (typeof activeCasterGetter === 'function' ? activeCasterGetter() : '')
        || sc.caster_id
        || '',
    ).trim();
    const casterEsc = escapeHtml(activeCasterId);

    let html = '';

    // ── permission-gated controls box (has_player_permission) ──
    // On ft3d_scene_host.php the page itself is the PHP-authorized encounter editor (and every save
    // re-authorizes), so surface the controls there even when the per-viewer permissions.player grant
    // isn't stamped on this blob — adventure/standalone encounters have no campaign GM grant. Mirrors
    // the ftIsModifyEncounterToolsPage() escape hatch the rest of the encounter-map already uses.
    if ((radialOnly || !inventoryUsesSceneRadial(arr) || !hasCampaignPermission(arr))
        && (hasPlayerPermission(arr) || (typeof ftIsModifyEncounterToolsPage === 'function' && ftIsModifyEncounterToolsPage()))) {
        const statusCount = targetStatusCount(arr);
        const hidden = inventoryIsHidden(arr);
        const isPrivate = Number(inventoryField(arr, 'inventory_private') ?? 0);

        const revealStyle = hidden ? 'display:block;' : 'display:none;';
        const hideStyle = hidden ? 'display:none;' : 'display:block;';
        const pubStyle = isPrivate === 0 ? 'display:block;' : 'display:none;';
        const privStyle = isPrivate === 1 ? 'display:block;' : 'display:none;';

        // MEDIA MANAGER — inventory pictures + videos (gallery + upload), replaces the legacy 4
        // buttons (upload/gallery × picture/video). Primary picture drives the map token + thumbnail
        // (collapse-to-primary; see ft_media_registry('inventory')). Parity with table_item_object_inc.php.
        const mediaManagerHref = escapeHtml(BASE_URL + 'tools/media/media_manager.php?type=inventory&id=' + encodeURIComponent(invId));

        // TURN-TO-RUBBLE — the warning→confirm pair before Delete. Show live, non-debris rows
        // optimistically; requestObjectDestruction uses the browser lane or refuses with a reason.
        const invDeleted = !!inventoryField(arr, 'inventory_deleted') || !!itemsCol(arr, 'item_deleted');
        const tplName = String(itemsCol(arr, 'item_name') ?? '');
        const alreadyRubble = /^Rubble,\s/.test(tplName);
        const showRubble = !invDeleted && !alreadyRubble;

        // WARES (BG3 mark-to-sell) toggle pair — same hide/show toggle-query pattern as reveal/hide.
        // Client gate is generous (creature-or-bag possessed + a caster in context); the endpoint is
        // the authority: it climbs to the ROOT owner and rejects unless it is the acting caster AND a
        // player_character, so an NPC-item click just returns a friendly warning.
        const waresPossRaw = (arr as { computed?: { possessor?: { type?: unknown } } })?.computed?.possessor?.type;
        const waresPossType = String((Array.isArray(waresPossRaw) ? waresPossRaw[0] : waresPossRaw) || '').toLowerCase();
        // Cache-rollover fallback: older caster blobs classified direct PC inventory as
        // encounter_creature. Permit that exact direct-owner shape only when the active caster's
        // cached root is demonstrably a player character; the endpoint remains authoritative and
        // independently climbs/validates the root owner.
        const possessId = String(inventoryField(arr, 'possess_id') ?? '').trim();
        const activeCaster = activeCasterId
            ? (window.FateTide?.casterCache?.get(activeCasterId) as Record<string, unknown> | undefined)
            : undefined;
        const activeCasterPcBucket = activeCaster?.player_characters;
        const activeCasterIsPc = !!activeCasterPcBucket
            && typeof activeCasterPcBucket === 'object'
            && !Array.isArray(activeCasterPcBucket)
            && Object.keys(activeCasterPcBucket as Record<string, unknown>).length > 0;
        const directActivePcOwned = activeCasterIsPc
            && possessId.toUpperCase() === activeCasterId.toUpperCase();
        const waresEligible = casterEsc !== '' && (
            waresPossType === 'player_character'
            || waresPossType === 'inventory'
            || directActivePcOwned
        );
        const isWaresOn = Number(inventoryField(arr, 'inventory_wares') ?? 0) === 1;
        const waresUrl = ''; // INV2: the pair commits through the flag envelope (inventoryFlagToggle); no legacy endpoint
        const possEsc = escapeHtml(String(inventoryField(arr, 'possess_id') ?? '')); // INV2: the holder the card showed (the flag commit's CAS)
        const waresCtx = `campaign_id=${campEsc}|encounter_id=${encEsc}|caster_id=${casterEsc}|inventory_id=${invIdEsc}|possess_id=${possEsc}`;
        const waresBtns = waresEligible
            ? `<button id="btn_wares_on_inventory_${invIdEsc}" type="button" class="btn btn-light border btn-sm" style="${isWaresOn ? 'display:none;' : 'display:block;'}" ${ocAttrs('toggle-query', { url: waresUrl, target: 'div_sidelog_warning', payload: `${waresCtx}|inventory_wares=1`, hideTarget: 'btn_wares_on_inventory_' + invIdEsc, showTarget: 'btn_wares_off_inventory_' + invIdEsc, showDisplay: 'block' })}>${iconMap('merchant', 25, 25, true, 'Add to wares (mark to sell at merchants)')}</button>`
                + `<button id="btn_wares_off_inventory_${invIdEsc}" type="button" class="btn btn-warning btn-sm" style="${isWaresOn ? 'display:block;' : 'display:none;'}" ${ocAttrs('toggle-query', { url: waresUrl, target: 'div_sidelog_warning', payload: `${waresCtx}|inventory_wares=0`, hideTarget: 'btn_wares_off_inventory_' + invIdEsc, showTarget: 'btn_wares_on_inventory_' + invIdEsc, showDisplay: 'block' })}>${iconMap('merchant', 25, 25, true, 'Remove from wares')}</button>`
            : '';
        const rubbleBtns = showRubble
            ? `<button type="button" class="btn btn-warning btn-sm" id="btn_turn_rubble_inventory_${invIdEsc}" ${ocAttrs('show-confirm', { hideTarget: 'btn_turn_rubble_inventory_' + invIdEsc, showTarget: 'btn_confirm_turn_rubble_inventory_' + invIdEsc })}>${iconMap('rubble', 25, 25, 'Turn to rubble (spills container contents, door becomes waypoint if applicable)')}</button>`
                + `<button type="button" class="btn btn-danger btn-sm" id="btn_confirm_turn_rubble_inventory_${invIdEsc}" style="display:none;" ${ocAttrs('query-and-close', { url: '', target: 'div_sidelog_warning', payload: `campaign_id=${campEsc}|encounter_id=${encEsc}|inventory_id=${invIdEsc}|caster_id=${casterEsc}` })}>${iconMap('confirm', 25, 25, 'Confirm turn to rubble')}</button>`
            : '';

        html += `
        <div class="p-2 border rounded mb-2">
            <!-- TOP ROW: edit inventory / gallery (media manager) / status effects -->
            <div class="d-flex align-items-center justify-content-start flex-wrap gap-1 mb-2">
                <div class="px-1">
                    <button class="btn btn-primary btn-sm" id="btn_edit_inventory_${invIdEsc}" ${ocAttrs('open-edit-inventory', { inventoryId: invIdEsc })}>${iconMap('edit_inventory')}</button>
                </div>
                <div class="px-1">
                    <a href="${mediaManagerHref}" id="btn_media_manager_inventory_${invIdEsc}" role="button" class="btn btn-info btn-sm">${iconMap('modify_picture_gallery', 25, 25, 'Media Manager - picture & video gallery + upload')}</a>
                </div>
                <div class="px-1 position-relative d-inline-block">
                    <a class="btn btn-primary btn-sm" role="button" ${ocAttrs('open-inventory-status-effects', { inventoryId: invIdEsc })}>${iconMap('status_effects')}</a>
                    ${statusCount > 0 ? `<span class="position-absolute top-0 start-75 translate-middle-x badge rounded-pill bg-white text-dark border border-secondary">${statusCount}<span class="visually-hidden">active status effects</span></span>` : ''}
                </div>
            </div>
            <!-- SECOND ROW: hide / private / delete -->
            <div class="d-flex align-items-center justify-content-start flex-wrap gap-1">
                <div class="px-1">
                    <button id="btn_reveal_creature_${invIdEsc}" type="button" class="btn btn-light border btn-sm" style="${revealStyle}" ${ocAttrs('toggle-query', { url: '', target: 'div_sidelog_warning', payload: `inventory_id=${invIdEsc}|encounter_id=${encEsc}|possess_id=${escapeHtml(String(inventoryField(arr, 'possess_id') ?? ''))}`, hideTarget: 'btn_reveal_creature_' + invIdEsc, showTarget: 'btn_hide_creature_' + invIdEsc, showDisplay: 'block' })}>${iconMap('reveal', 25, 25, true, 'Reveal Inventory')}</button>
                    <button id="btn_hide_creature_${invIdEsc}" type="button" class="btn btn-dark border btn-sm" style="${hideStyle}" ${ocAttrs('toggle-query', { url: '', target: 'div_sidelog_warning', payload: `inventory_id=${invIdEsc}|encounter_id=${encEsc}|possess_id=${escapeHtml(String(inventoryField(arr, 'possess_id') ?? ''))}`, hideTarget: 'btn_hide_creature_' + invIdEsc, showTarget: 'btn_reveal_creature_' + invIdEsc, showDisplay: 'block' })}>${iconMap('hidden', 25, 25, true, 'Hide Inventory')}</button>
                </div>
                <div class="px-1">
                    <button id="inventory_public_${invIdEsc}" type="button" class="btn btn-primary btn-sm" style="${pubStyle}" ${ocAttrs('toggle-query', { url: BASE_URL + 'options/db_queries/update/update_privacy_inc.php', target: 'div_sidelog_warning', payload: `item_type=inventory|inventory_id=${invIdEsc}|inventory_private=1`, hideTarget: 'inventory_public_' + invIdEsc, showTarget: 'inventory_private_' + invIdEsc, showDisplay: 'block' })}>${iconMap('public')}</button>
                    <button id="inventory_private_${invIdEsc}" type="button" class="btn btn-danger btn-sm" style="${privStyle}" ${ocAttrs('toggle-query', { url: BASE_URL + 'options/db_queries/update/update_privacy_inc.php', target: 'div_sidelog_warning', payload: `item_type=inventory|inventory_id=${invIdEsc}|inventory_private=0`, hideTarget: 'inventory_private_' + invIdEsc, showTarget: 'inventory_public_' + invIdEsc, showDisplay: 'block' })}>${iconMap('private')}</button>
                </div>
                ${waresBtns ? `<div class="px-1">${waresBtns}</div>` : ''}
                <div class="ms-auto d-flex align-items-center flex-wrap gap-1">
                    ${rubbleBtns}
                    <button class="btn btn-danger btn-sm" id="btn_delete_inventory_${invIdEsc}" ${ocAttrs('delete-inventory', { payload: `encounter_id=${encEsc}|inventory_id=${invIdEsc}|caster_id=${casterEsc}` })}>${iconMap('delete_inventory')}</button>
                </div>
            </div>
        </div>`;
    }

    // ── carry verbs (owner 2026-09-15): Drop / Pick up / Throw for the acting character. Held rows drop
    // and throw; loose floor rows pick up and throw. Rendered in radial mode too — the FT3D radial reads
    // these selectors (scene-radial.ts). Janus / the move endpoint are the authority on reach, weight, AP.
    if (activeCasterId !== '' && !invDeletedFlag(arr)) {
        const possessId = String(inventoryField(arr, 'possess_id') ?? '').toUpperCase();
        const sceneHost = window as Window & { ftGetActiveEncounterId?: () => string };
        const heldByActor = possessId === activeCasterId.toUpperCase() || ftCasterHoldsRow(activeCasterId, invId);
        const onFloor = !heldByActor && possessId !== '' && possessId === String(sceneHost.ftGetActiveEncounterId?.() ?? sc.encounter_id ?? '').toUpperCase();
        const carryable = !NON_THROWABLE_CARD_TYPES.has(type) && !layerHas(arr, 'immovable');
        if (carryable && (heldByActor || onFloor)) {
            // The disabled buttons keep their action attrs: the scene radial finds its verbs by these selectors and
            // greys them itself from the same plan (scene-radial.ts).
            const throwPlan = throwObjectPlan(invId);
            html += `<div class="ft-oc-action-row d-flex align-items-center justify-content-start flex-wrap border rounded p-2 mb-2 gap-3">`
                + (heldByActor ? `<button class="btn btn-info btn-sm" title="Drop at your feet" ${ocAttrs('drop-inventory', { inventoryId: invIdEsc })}>${iconMap('drop_inventory')}</button>` : '')
                + (onFloor ? (throwPlan.freeHand === false
                    ? `<button class="btn btn-info btn-sm" title="Your hands are full — free a hand to pick that up." disabled aria-disabled="true" ${ocAttrs('pick-up-inventory', { inventoryId: invIdEsc })}>${iconMap('move_inventory')}</button>`
                    : `<button class="btn btn-info btn-sm" title="Pick up" ${ocAttrs('pick-up-inventory', { inventoryId: invIdEsc })}>${iconMap('move_inventory')}</button>`) : '')
                // Throw vs Draw & throw vs refused, from the one throw gate (owner 2026-09-16): in hand throws, carried is drawn first.
                + (throwPlan.ok
                    ? `<button class="btn btn-info btn-sm" title="${throwPlan.mode === 'draw' ? 'Draw & throw (to a free hand first)' : throwPlan.mode === 'telekinesis' ? 'Telekinesis — move or hurl it from where it lies' : 'Throw'}" ${ocAttrs('throw-inventory', { inventoryId: invIdEsc })}>${iconMap('throw_inventory')}</button>`
                    : `<button class="btn btn-info btn-sm" title="${escapeHtml(throwPlan.reason ?? 'Cannot throw that.')}" disabled aria-disabled="true" ${ocAttrs('throw-inventory', { inventoryId: invIdEsc })}>${iconMap('throw_inventory')}</button>`)
                + `</div>`;
        }
    }

    if (radialOnly) return html;
    // ── action row: consume + disenchant + browser book actions ──
    html += renderActionRow(arr);

    if (type === 'light_source') {
        html += `<div class="ft-oc-action-row d-flex align-items-center justify-content-start flex-wrap border rounded p-2 mb-2 gap-3">${islandPlaceholder('light_controls', invId, true)}</div>`;
    }
    if (
        nodeHasStatusItemId(arr, 'target', LOCKED_STATUS_ITEM_ID)
        || nodeHasStatusItemId(arr, 'caster', LOCKED_STATUS_ITEM_ID)
    ) {
        html += islandPlaceholder('lock_controls', invId);
    }
    // Gathering is immediate. Only excavation needs the island's saved attempt facts.
    const gatherKind = gatherControlKind(arr);
    if (gatherKind) html += gatherKind === 'gather' ? gatherControlsHtml(arr, null) : islandPlaceholder('gather_controls', invId);
    // Crafting Phase-3 WIP: an "Unfinished Item" carrying the crafting_progress tracker gets the
    // craft island (server-rendered — recipe/progress read authoritatively off the status row;
    // step 1 is read-only, Work/Stop controls arrive with the step-2 endpoints). WS updates are
    // free: settle writes propagate the inventory target's possessor chain → card re-render →
    // island re-fetch.
    if (nodeHasStatusItemId(arr, 'target', CRAFTING_PROGRESS_STATUS_ITEM_ID)) {
        html += islandPlaceholder('craft_controls', invId);
    }

    // ── GM container random-generator widget ──
    // Parity with table_item_object_inc.php:1401-1408 (has_campaign_permission && is_inventory_arr_a_container).
    // Rendered entirely client-side and INSTANTLY — the only DB-backed parts (the skill-book + iconic-book
    // dropdown option lists) load on-demand the first time the GM expands the panel (ftWireContainerLootGen
    // in container-loot-gen.ts, hydrated by renderObjectCard). Placed before the contents grid (PHP order).
    if (isContainerArr(arr) && hasCampaignPermission(arr) && !inventoryUsesSceneRadial(arr)) {
        html += buildContainerLootGenHtml(invId);
    }

    // Raw links stay available to every engine. Only the display island observes viewer visibility.
    const visibleLinks = visibleInventoryEncounterLinks(arr);
    if (visibleLinks.length) {
        const knownLinks = visibleInventoryEncounterLinks(arr, findInventoryInstanceNode, new Set());
        html += islandPlaceholder('encounter_links', invId, false, knownLinks.length !== visibleLinks.length);
    }

    // ── RELOCK with key (JSON truth, zero DB) ──
    // An UNLOCKED row always renders on this cache path (only locked rows fall back to the
    // server), so the relock control lives here: shown when the ACTIVE caster carries a key
    // whose computed.key_links.opens includes this row — answered entirely from the cached
    // caster blob (findCasterKeyForLock). Clicking posts key_action='lock' to use_key_inc.php,
    // which re-validates possession + the link row and applies `locked` self-cast on the row.
    if (
        !nodeHasStatusItemId(arr, 'target', LOCKED_STATUS_ITEM_ID)
        && !nodeHasStatusItemId(arr, 'caster', LOCKED_STATUS_ITEM_ID)
    ) {
        const relockKey = findCasterKeyForLock(String(sc.caster_id ?? ''), [invId]);
        if (relockKey.casterResolved && relockKey.keyInventoryId) {
            html += `
        <div class="d-flex align-items-center justify-content-start border rounded p-2 flex-wrap gap-2 mb-2">
            <div class="pl-2 pr-2">
                <button
                    type="button"
                    id="btn_lock_keys_${invIdEsc}"
                    class="btn btn-warning btn-sm"
                    ${ocAttrs('use-key-lock', { inventoryId: invIdEsc })}
                >${iconMap('lock', 25, 25, 'Lock this with the key you are carrying.')}</button>
            </div>
            <span class="small text-muted" id="lock_keys_result_${invIdEsc}">Lock with ${escapeHtml(relockKey.keyName || 'your key')}</span>
        </div>`;
        }
    }

    // ── Trap controls (Trap minigame wave 2026-07-31) ──
    // Disarm renders ONLY when the armed Trapped row is visible to this viewer (GM, or the row
    // flipped public via a successful discern / a visible firing) — that visibility gate IS the
    // armed-state redaction. Discern renders on any trappable host; Arm on a disarmed trap object.
    if (!inventoryUsesSceneRadial(arr)) html += renderTrapControls(arr, invIdEsc);

    // ── GM key ↔ lock link ties (computed.key_links off the cached blob) ──
    // Parity with the table_item_object_inc.php "Key Links (GM only)" box. Keys are never locked,
    // so their cards render on THIS cache path and never reach the PHP section — read the compile
    // seam's computed.key_links (opens / unlocked_by) instead of the table. Locked rows fall back
    // to the server (computeInventoryRenderPlan), where the PHP box queries authoritatively.
    html += renderKeyLinksGm(arr);

    html += renderContainerAccess(arr);

    return html;
}

/** Shared contents and reach gates for item cards and standalone container windows. */
export function renderContainerAccess(arr: ObjectCardBlob): string {
    const invIdEsc = escapeHtml(String(inventoryField(arr, 'inventory_id') ?? ''));
    let html = '';
    // ── container contents ──
    // Reach-gate the CONTENTS for player eyes — including a GM "seeing through" a controlled creature.
    // window.ftActiveViewerCanReach (encounter-map bundle, xz/reach-capability.ts) returns tri-state:
    //   true  → full-GM / carried container (possession) / in reach → show the items.
    //   false → viewer definitely out of reach → block them out.
    //   null  → encounter page but viewer/widget unresolved (bootstrap race) → fail CLOSED (block); the
    //           card re-renders on caster_update so a transient null self-heals.
    // Absent global (library / any non-encounter page has no encounter-map bundle) → unblocked.
    if (isContainerArr(arr)) {
        // BURIED DIGSITE gate (before reach): a digsite's contents stay underground for every
        // player eye — reach is irrelevant, only a successful excavation releases them. Full GM
        // (and the modify_encounter editor) still inspects/curates the buried prize. Fail closed:
        // absent/unresolved GM capability renders the buried placeholder. Server teeth =
        // move_draggable_inventory_inc.php's digsite guard.
        if (itemType(arr) === 'digsite' && !(typeof window.ftActiveViewerIsFullGm === 'function' && window.ftActiveViewerIsFullGm() === true)) {
            html += renderContainerContentsBuried();
        } else {
            const canReach = window.ftActiveViewerCanReach;
            const contentsBlocked = typeof canReach === 'function' ? (canReach(arr) !== true) : false;
            // TRAP `open` verb (Trap system 2026-07-30): a NON-GM viewer actually seeing the
            // contents IS the open — ping the server verb (fire-and-forget, once per row per
            // session). Unarmed/untrapped hosts no-op server-side in two cheap reads.
            if (!contentsBlocked && !hasCampaignPermission(arr)) {
                ftPingTrapOpen(String(inventoryField(arr, 'inventory_id') ?? ''));
            }
            // CORPSE-AS-INVENTORY (2026-09-23): a body also shows what its creature still wears and
            // carries, proxied through the link. Same reach gate — out of reach, nothing is rendered. The
            // body's OWN contents (things laid on it) show only when there are some.
            const corpseSections = contentsBlocked ? '' : renderCorpseBodySections(arr);
            const ownEntries = arr.inventory_in_container;
            const ownEmpty = !(Array.isArray(ownEntries) ? ownEntries.length : ownEntries);
            if (!(corpseSections && ownEmpty)) html += renderContainerContents(arr, invIdEsc, contentsBlocked);
            html += corpseSections;
        }
    }

    return html;
}

// ── Trap verbs: Arm / Disarm / Discern (Trap minigame wave 2026-07-31) ─────────────
const TRAPPED_STATUS_ITEM_ID = '78C51D2D-5178-551C-1249-B5DDA4083098'; // StatusEffect::getId('trapped')

/** Object types worth offering trap verbs on — the trappable hosts + dedicated trap objects. */
function ftTrapHostEligible(arr: ObjectCardBlob): boolean {
    const type = itemType(arr);
    return type === 'trap' || type === 'door' || type === 'lock' || isContainerArr(arr);
}

/**
 * Armed = the permanent self-cast Trapped row on the host (the relock pattern). Reads both bucket
 * shapes (array / record) and both row shapes (def on row / nested status_arr / bare
 * status_item_id), mirroring PHP's lockpicking_status_row_matches. anyPublic distinguishes a
 * GM-only (status_private) armed row from one the party has discerned.
 */
function ftTrappedArmedInfo(arr: ObjectCardBlob): { armed: boolean; anyPublic: boolean } {
    let armed = false;
    let anyPublic = false;
    const statuses = recordOf((arr as { statuses?: unknown }).statuses);
    if (!statuses) return { armed, anyPublic };
    for (const mode of ['caster', 'target']) {
        const bucket = statuses[mode];
        const rows: unknown[] = Array.isArray(bucket)
            ? bucket
            : (recordOf(bucket) ? Object.values(recordOf(bucket) as Record<string, unknown>) : []);
        for (const row of rows) {
            const rec = recordOf(row);
            if (!rec) continue;
            // Nullish-chain carefully: a failed nested lookup must yield undefined, never '' —
            // an '' short-circuits ?? and hides the bare status_item_id shape (Codex 2026-07-31 #3).
            const rowId = String(
                itemsCol(row, 'item_id')
                ?? rec.item_id
                ?? recordOf(rec.status_arr)?.item_id
                ?? rec.status_item_id
                ?? '',
            ).toUpperCase();
            if (rowId !== TRAPPED_STATUS_ITEM_ID) continue;
            armed = true;
            const privRaw = rec.status_private;
            const priv = Array.isArray(privRaw) ? privRaw[0] : privRaw;
            if (Number(priv ?? 0) !== 1) anyPublic = true;
        }
    }
    return { armed, anyPublic };
}

/**
 * The trap verb strip: Disarm (opens the minigame) when the armed row is visible to this viewer;
 * Discern (button + the card double-click gesture) on any trappable host still unrevealed; Arm on
 * a disarmed dedicated trap object. The full GM can trigger an armed host against one creature.
 */
export function renderTrapControls(arr: ObjectCardBlob, invIdEsc: string): string {
    if (!ftTrapHostEligible(arr)) return '';
    const info = ftTrappedArmedInfo(arr);
    const isGm = hasCampaignPermission(arr);
    if (isGm && !info.armed && itemType(arr) !== 'trap') return '';
    const showDisarm = info.armed && (isGm || info.anyPublic);
    const showDiscern = !(info.armed && info.anyPublic); // once revealed, the study is done
    const showArm = itemType(arr) === 'trap' && !info.armed;
    if (!showDisarm && !showDiscern && !showArm) return '';

    let btns = '';
    if (showDisarm) {
        btns += `<button type="button" class="btn btn-danger btn-sm" ${ocAttrs('trap-disarm', { inventoryId: invIdEsc })} title="Work the trap with the tools its nature demands. Botch badly and it fires at you.">Disarm Trap</button>`;
    }
    if (showArm) {
        const actor = (window as Window & { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.();
        const unavailable = !actor && window.ftActiveViewerIsFullGm?.() !== true;
        btns += `<button type="button" class="btn btn-warning btn-sm" ${ocAttrs('trap-arm', { inventoryId: invIdEsc })} ${unavailable ? 'disabled' : ''} title="${unavailable ? 'Control a character first' : 'Set the trap: it arms where it sits.'}">Arm Trap</button>`;
    }
    if (info.armed && window.ftActiveViewerIsFullGm?.() === true) {
        btns += `<button type="button" class="btn btn-danger btn-sm" ${ocAttrs('trap-trigger', { inventoryId: invIdEsc })}>Trigger Trap</button>`;
    }
    if (showDiscern) {
        btns += `<button type="button" class="btn btn-outline-secondary btn-sm" ${ocAttrs('trap-discern', { inventoryId: invIdEsc })} title="Study the object for trap-work (double-clicking the card does this too). Reach-gated; 3 AP in combat.">Discern Trap</button>`;
    }
    const note = showDisarm ? 'There is trap-work here.' : '';
    return `<div class="d-flex align-items-center justify-content-start border rounded p-2 flex-wrap gap-2 mb-2">`
        + btns
        + `<span class="small text-muted" id="trap_verb_result_${invIdEsc}">${escapeHtml(note)}</span>`
        + `</div>`;
}

/** POST a trap verb endpoint (JSON body — get_data() reads a JSON request body ONLY). */
const ftTrapVerbInFlight = new Set<string>();
async function ftTrapVerb(endpoint: string, hostInventoryId: string, feedback?: (message: string) => void): Promise<void> {
    if (!hostInventoryId) return;
    // One request per verb+host at a time: a double-click on the Discern BUTTON is two click
    // events, and each one costs 3 AP in combat (Codex 2026-07-31 #4).
    const flightKey = endpoint + '|' + hostInventoryId;
    if (ftTrapVerbInFlight.has(flightKey)) return;
    ftTrapVerbInFlight.add(flightKey);
    const sc = (typeof getSidelogContext === 'function' ? (getSidelogContext() || {}) : {}) as Record<string, unknown>;
    const activeCasterGetter = (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId;
    const casterId = String(
        (typeof activeCasterGetter === 'function' ? activeCasterGetter() : sc.caster_id)
        || '',
    ).trim();
    const out = document.getElementById(`trap_verb_result_${hostInventoryId}`);
    const say = (t: string): void => { if (out) out.textContent = t; feedback?.(t); };
    say('…');
    try {
        const fullGm = (window as unknown as { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() === true;
        if (!casterId && !(endpoint === 'arm_trap_inc.php' && fullGm)) { say('No acting character.'); return; }
        const within = endpoint === 'arm_trap_inc.php' && fullGm ? true : await actorCanReachTarget(casterId, hostInventoryId);
        if (within !== true) {
            say(within === false ? 'You need to be closer.' : 'Could not determine reach. Refresh the encounter and try again.');
            return;
        }
        // CLIENT TRUTH (Lane TRAPS S1, 2026-09-28): window.ftTryCommitTrapVerb (websocket/trap-truth.ts → trap_truth_commit_inc.php) —
        // the page picks the tool row and rolls the check; the retired arm_trap / discern_trap endpoints are never called.
        const commit = (window as unknown as { ftTryCommitTrapVerb?: (input: { verb: 'arm' | 'discern'; encounterId: string; actorId: string; hostInventoryId: string; rollPrivate?: number; gm?: boolean })
            => Promise<{ outcome: 'handled'; success: boolean } | { outcome: 'refused'; error: string; message: string } | null> }).ftTryCommitTrapVerb;
        const outcome = typeof commit === 'function'
            ? await commit({ verb: endpoint.startsWith('discern') ? 'discern' : 'arm', encounterId: String(sc.encounter_id ?? ''), actorId: casterId || String(sc.encounter_id ?? ''), hostInventoryId,
                rollPrivate: Number(sc.roll_private ?? 0) || 0, gm: fullGm })
            : null;
        if (!outcome) { say('Could not act on this object here. Refresh the encounter and try again.'); return; }
        if (outcome.outcome === 'refused') {
            say(outcome.error === 'not_armed' && endpoint.startsWith('discern') ? 'You find no trap-work here.' : outcome.message);
            return;
        }
        if (endpoint.startsWith('discern')) {
            say(outcome.success ? 'You read the work — it IS trapped.' : 'You cannot make sense of it. (You can study it again.)');
        } else {
            say(outcome.success ? 'Armed.' : 'You fumble the mechanism — it will not set. (You can try again.)');
        }
    } catch {
        say('Connection lost.');
    } finally {
        ftTrapVerbInFlight.delete(flightKey);
    }
}

// Scene radials invoke the same checked action as the object card, with visible scene feedback.
Object.assign(window, { ftDiscernInventoryTrap: (id: string, feedback?: (message: string) => void) => ftTrapVerb('discern_trap_inc.php', id, feedback) });

// The DOUBLE-CLICK discern gesture: double-clicking anywhere on a trappable host's object card is
// the in-fiction "study it closely". The card root carries data-ft-trap-card (buildObjectCardHtml)
// only for eligible hosts; the browser owns reach, the endpoint owns AP, and a plain not_armed answer is the honest
// "you find nothing". Buttons/links inside the card keep their own single-click behaviors — a
// double-click on them is almost always two intentional clicks, so those are excluded.
document.addEventListener('dblclick', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('button, a, input, select, textarea, [data-ft-oc-action]')) return;
    const card = target.closest<HTMLElement>('[data-ft-trap-card]');
    const hostId = card?.dataset.ftTrapCard || '';
    if (!hostId) return;
    void ftTrapVerb('discern_trap_inc.php', hostId);
});

// ── Trap `open` verb ping (Trap system 2026-07-30) ─────────────────────────────────
// POST home for the open interaction so the trap side effect never rides a read path —
// options/game/traps/open_object_inc.php runs ft_trap_check_interaction('open'). Session-deduped
// per inventory row: the armed Trapped status is the real rate limit server-side, this just keeps
// re-renders from spamming the endpoint.
const ftTrapOpenPinged = new Set<string>();
async function ftPingTrapOpen(hostInventoryId: string): Promise<void> {
    if (!hostInventoryId || ftTrapOpenPinged.has(hostInventoryId)) return;
    const sc = (typeof getSidelogContext === 'function' ? (getSidelogContext() || {}) : {}) as Record<string, unknown>;
    const activeCasterGetter = (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId;
    const casterId = String(
        (typeof activeCasterGetter === 'function' ? activeCasterGetter() : '')
        || sc.caster_id
        || '',
    ).trim();
    if (!casterId) return; // no acting caster → no interactor to spring it on
    ftTrapOpenPinged.add(hostInventoryId);
    if (await actorCanReachTarget(casterId, hostInventoryId) !== true) {
        ftTrapOpenPinged.delete(hostInventoryId);
        return;
    }
    // CLIENT TRUTH (Lane TRAPS S4, 2026-09-28): window.ftTryCommitTrapOpen (websocket/trap-truth.ts → trap_truth_commit_inc.php,
    // action trap_open) — authority from the host's own encounter, the host row patched on a firing; open_object_inc.php is retired.
    const open = (window as unknown as { ftTryCommitTrapOpen?: (input: { encounterId: string; actorId: string; hostInventoryId: string }) => Promise<{ ok: boolean }> }).ftTryCommitTrapOpen;
    if (typeof open !== 'function') { ftTrapOpenPinged.delete(hostInventoryId); return; }
    try {
        const result = await open({ encounterId: String(sc.encounter_id ?? ''), actorId: casterId, hostInventoryId });
        if (!result.ok) ftTrapOpenPinged.delete(hostInventoryId);
    } catch {
        ftTrapOpenPinged.delete(hostInventoryId);
    }
}

// ── GM key ↔ lock links box (computed.key_links) ──────────────────────────────────
// The compile seam (build_inventory_arr.php) stamps computed.key_links on keys (opens: [lock
// inventory ids]) and on locked rows (unlocked_by: [key inventory ids]). GM/admin eyes only —
// same has_campaign_permission gate as the PHP box. Counterpart names resolve from the client
// caches when the linked row is cached (same encounter usually is); a cache miss still renders
// a clickable row (openItemInfo falls back to the server) with a generic label.
function keyLinkCounterpartName(id: string): string {
    const node = findInventoryInstanceNode(id);
    if (node) {
        const nm = String(
            inventoryField(node, 'inventory_name')
            ?? itemsCol(node, 'item_name')
            ?? '',
        );
        if (nm !== '') return nm;
    }
    return 'Linked item';
}
function renderKeyLinksGm(arr: ObjectCardBlob): string {
    if (!hasCampaignPermission(arr)) return '';
    const keyLinks = computedRecord(arr, 'key_links');
    if (!keyLinks) return '';
    const rows: Array<{ label: string; id: string }> = [];
    for (const id of (Array.isArray(keyLinks.opens) ? keyLinks.opens : [])) {
        if (typeof id === 'string' && id !== '') rows.push({ label: 'Opens', id });
    }
    for (const id of (Array.isArray(keyLinks.unlocked_by) ? keyLinks.unlocked_by : [])) {
        if (typeof id === 'string' && id !== '') rows.push({ label: 'Unlocked by', id });
    }
    if (rows.length === 0) return '';
    const body = rows.map((r) =>
        `<div class="small">${r.label}: <span role="button" class="cursor-pointer text-decoration-underline" `
        + `${ocInfoAttrs(r.id)}>${escapeHtml(keyLinkCounterpartName(r.id))}</span></div>`,
    ).join('');
    return `
        <div class="border rounded p-2 mt-2 mb-2">
            <h6 class="mb-1">${iconMap('key', 18, 18)} Key Links <span class="small text-muted">(GM only)</span></h6>
            ${body}
        </div>`;
}

// Consume/disenchant use their native bridges. Book chrome uses this card's held
// object state and the existing book activity/history preview.
function renderActionRow(arr: ObjectCardBlob): string {
    const type = itemType(arr);
    const inv = isInventory(arr);
    const invIdEsc = escapeHtml(String(inventoryField(arr, 'inventory_id') ?? ''));
    const showConsume = inv && (layerHas(arr, 'consumable') || type === 'food' || type === 'drink');
    const activeId = (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.() ?? '';
    const actor = recordOf(window.FateTide?.casterCache?.get(activeId));
    const showDisenchant = inv && itemCardCanDisenchant(arr, actor);
    const bookMaybe = hasBookReadingMechanics(arr);
    if (!showConsume && !showDisenchant && !bookMaybe) return '';
    const consumeBtn = showConsume
        ? `<button class="btn btn-info btn-sm" ${ocAttrs('consume', { inventoryId: invIdEsc })}>${iconMap('consume')}</button>`
        : '';
    const disenchantBtn = showDisenchant
        ? `<button class="btn btn-info btn-sm" ${ocAttrs('disenchant', { inventoryId: invIdEsc })}>${iconMap('disenchant_inventory')}</button>`
        : '';
    const bookActions = bookMaybe ? bookActionMountHtml(arr) : '';
    return `
        <div class="ft-oc-action-row d-flex align-items-center justify-content-start flex-wrap border rounded p-2 mb-2 gap-3">
            ${consumeBtn}
            ${disenchantBtn}
            ${bookActions}
        </div>`;
}

/** Build one card; the caller owns its mount and interactive hydration. */
export function buildObjectCardHtml(value: unknown, names: ReferencedNames): string {
    if (entityBasePending(value)) return ENTITY_CARD_LOADING;
    if (value && typeof value === 'object' && '_entity_refusal' in value && typeof value._entity_refusal === 'string') {
        const row = recordOf(value);
        return `<div class="alert alert-warning" role="alert">${escapeHtml(value._entity_refusal)}</div>${row ? renderInventoryControls(row) : ''}`;
    }
    if (value && typeof value === 'object' && '_inventory_refusal' in value && typeof value._inventory_refusal === 'string') {
        return `<div class="alert alert-warning" role="alert">${escapeHtml(value._inventory_refusal)}</div>`;
    }
    const arr = recordOf(value);
    if (!arr) return '';
    const cardCol = 'col-md-12';
    // Rune-eligible cards get the .ft-rune-card scope class (positions/styles the medallion via
    // css/style.css) + the medallion mount at the top of the card body.
    const runeCardClass = runeIsEligible(arr) ? ' ft-rune-card' : '';
    // Trappable hosts carry the double-click-discern gesture root (Trap minigame wave 2026-07-31).
    const trapCardAttr = isInventory(arr) && ftTrapHostEligible(arr)
        ? ` data-ft-trap-card="${escapeHtml(String(inventoryField(arr, 'inventory_id') ?? ''))}"`
        : '';
    return `<div class="card ${cardCol}${runeCardClass}"${trapCardAttr}>
    ${renderCardMedia(arr)}
    <div class="card-body text-left">
        ${runeIconMountHtml(arr, 96)}
        ${renderNameRow(arr, names)}
        ${isInventory(arr) ? renderInventoryControls(arr) : renderActionRow(arr)}
        ${renderDescription(arr)}
        ${renderPropertyList(arr, names)}
        ${renderEnvironment(arr)}
    </div>
    ${renderSourceFooter(arr)}
</div>`;
}
